import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ComponentRef,
  ElementRef,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  Type,
  ViewChild,
} from '@angular/core';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { BehaviorSubject, combineLatest, EMPTY, forkJoin, from, interval, merge, Observable, of, ReplaySubject, Subject } from 'rxjs';
import {
  distinctUntilChanged,
  filter,
  map,
  mergeMap,
  skip,
  switchMap,
  take,
  takeUntil,
  tap,
  toArray,
  withLatestFrom,
} from 'rxjs/operators';

import {
  AddressModel,
  CurrencyModel,
  CurrencyRateModel,
  IncoTermModel,
  ShipmentModel,
  ValidationMessageModel,
} from '@data-layer/models/common';
import { enumEsEditPageType } from '@enum/enumEsEditPageType';
import {
  enumEntrySummaryDutiesPaymentType,
  enumEntrySummaryState,
  enumEntrySummarySubmissionType,
  enumTransportationMode,
} from '@data-layer/enums/entry-summaries';
import { CompaniesService } from '@services/companies.service';
import { DEFAULT_CURRENCY_CODE } from '@constants/currency';
import { GoogleAnalyticsService } from '@services/google-analytics.service';
import { enumCompanyPaymentType } from '@data-layer/enums/companies';
import { DocumentCreateModel, DocumentModel } from '@data-layer/models/documents';
import { GenericService } from '@services/generic.service';
import {
  CodeRepository,
  CurrenciesRepository,
  DeliveryOrdersRepository,
  EntrySummaryMessagesRepository,
  EntrySummaryRepository,
  InvoicesRepository,
} from '@data-layer/repositories';
import {
  DeliveryOrderModel,
  EntrySummaryDutiesModel,
  EntrySummaryModel,
  InvoiceModel,
  LineModel,
} from '@data-layer/models/entry-summaries';
import { IEsValidationData } from '@interfaces/es-validation-data';
import { AffiliateData, ICustomError } from '@shared/result/interfaces';
import { EntrySummaryStateService, EsCreateBlFormService, EsStepsService, EsValidationsHelperService } from '../../services';
import { EntrySummaryActionsHelper } from '@helpers/entry-summary-actions.helper';
import { EntrySummaryStatusHelper } from '@helpers/entry-summary-status.helper';
import { BillItemPendingModel, BillModel, PaymentBillItemPendingModel } from '@data-layer/models/payments';
import {
  enumDocumentRelatedEntityType,
  enumDocumentRootEntityType,
  enumDocumentState,
  enumDocumentType,
} from '@data-layer/enums/documents';
import { DeliveriesOrderService } from '@app/modules/delivery-order/services';
import { IEsDocumentStateUpdate } from '@shared/es-mutual/interfaces';
import { EsEditShipmentDesktopComponent } from '../desktop/es-edit-shipment-desktop/es-edit-shipment-desktop.component';
import { TranslateService } from '@ngx-translate/core';
import { EsInvoiceFormDesktopComponent } from '../desktop/es-invoice-form-desktop/es-invoice-form-desktop.component';
import { DateHelper } from '@helpers/date.helper';
import { UntypedFormArray, UntypedFormControl, UntypedFormGroup, ValidationErrors, Validators } from '@angular/forms';
import { FormHelper } from '@helpers/form.helper';
import { CustomDateByUtcPipe } from '@shared/pipes-shared/pipes';
import { MissingFieldsComponent } from '@shared/missing-fields/components';
import { EntityModel } from '@data-layer/models/entities';
import { enumEntityModelCode } from '@data-layer/enums/entities';
import { EsEntityUnifyDefaultDesktopComponent } from '../desktop/es-entity-unify-default-desktop/es-entity-unify-default-desktop.component';
import { DESC_ENTITY } from '@constants/desc-entity';
import { enumIncotermApplicableType, enumOcrStatus, enumValidationMessageSeverity } from '@data-layer/enums/common';
import { EsLineItemFormDesktopComponent } from '../desktop/es-line-item-form-desktop/es-line-item-form-desktop.component';
import { EsHtsLinePanelComponent } from '../desktop/es-hts-line-panel/es-hts-line-panel.component';
import { CompanyModel } from '@data-layer/models/companies';
import { DocumentsService } from '@services/documents.service';
import { ManagementEntityEventModel } from '@data-layer/models/management';
import { enumGATagEventCategory } from '@enum/enumGATagEventCategory';
import { enumGATagEventAction } from '@enum/enumGATagEventAction';
import { IPaymentBreakdownItemGroup } from '@interfaces/payment-breakdown-group';
import { enumPaymentState } from '@data-layer/enums/payments';
import { EsShipmentDetailsDesktopComponent } from '../desktop/es-shipment-details-desktop/es-shipment-details-desktop.component';
import { EsTermsOfSaleDesktopComponent } from '../desktop/es-terms-of-sale-form-desktop/es-terms-of-sale-desktop.component';
import { EsBillOfLadingFormDesktopComponent } from '../desktop/es-bill-of-lading-form-desktop/es-bill-of-lading-form-desktop.component';
import { EsDutiesTypeDesktopComponent } from '../desktop/es-duties-type-form-desktop/es-duties-type-desktop.component';
import { EsBondsDesktopComponent } from '../desktop/es-bonds-form-desktop/es-bonds-desktop.component';
import { IS_CREATING_ES_INVOICE_KEY, IS_CREATING_ES_KEY } from '../../const/entry-summary.const';
import { CustomValidator } from '@helpers/custom-validator';
import { EsChatDesktopComponent } from '../desktop/es-chat-desktop/es-chat-desktop.component';
import { enumUserBetaFlag } from '@data-layer/enums/user';
import { AppLayoutService } from '@app/modules/app-layout/app-layout.service';
import { MarineInsuranceSideformService, MarineInsuranceState } from '@shared/marine-insurance-shared/services';
import { enumMarineInsuranceEntityType, enumMarineInsurancePendingPaymentAction } from '@data-layer/enums/marine-insurance';
import { MarineInsuranceModel } from '@data-layer/models/marine-insurance';
import { flatten } from 'lodash-es';
import { UtilsHelper } from '@helpers/utils-helper';
import { isPoaBill } from '@helpers/bill.helper';
import { PaymentState } from '@app/modules/payment/services';
import { HtsLinesReviewService } from '@shared/hts-assistant/services/hts-lines-review.service';
import { AppService } from '@app/app.service';
import { UserBetaFlagModel } from '@data-layer/models/user';
import { EsInvoiceStepperComponent } from '@shared/es-mutual/components';
import { EsPoaSidebarComponent } from '../es-poa-sidebar/es-poa-sidebar.component';
import { enumWizardComponent } from '@enum/enumWizardComponent';
import { enumMarineInsuranceStatusType } from '@app/data-layer/enums/marine-insurance/enumInsuranceStatusType.enum';
import { enumMimeType } from '@enum/enumMimeType';
import { ALLOWED_FILE_EXTENSIONS, ALLOWED_FILE_MIMES } from '@constants/constants';
import { enumFileExtension } from '@enum/enumFileExtension';
import { environment } from '@env/environment';
import { Location } from '@angular/common';

@Component({
  selector: 'es-summary',
  templateUrl: './summary.component.html',
  styleUrls: ['./summary.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SummaryComponent implements OnInit, OnDestroy {
  @Input() public formScrollClass: string;
  @Input() public paymentBreakdown: IPaymentBreakdownItemGroup[] = [];
  @Input() public isSidebar: boolean;
  @Input() public isMonthlyInstantDuties: boolean;
  @Input() public isLoading: boolean;
  @Output() public onCancelPayment: EventEmitter<any> = new EventEmitter<any>();
  @Output() public onSuccessPayment: EventEmitter<{ state: enumPaymentState; bill: BillModel }> = new EventEmitter<{
    state: enumPaymentState;
    bill: BillModel;
  }>();
  @Output() public onErrorPayment: EventEmitter<any> = new EventEmitter<any>();
  @Output() public onErrorCustomPayment: EventEmitter<any> = new EventEmitter<any>();
  @Output() public onAddToCard: EventEmitter<any> = new EventEmitter<any>();
  @Output() public onConfirmDuties: EventEmitter<void> = new EventEmitter<void>();
  @Output() public editInvoiceChange: EventEmitter<boolean> = new EventEmitter<boolean>();

  @ViewChild('additionalDocumentsInput', { static: true }) public additionalDocumentsInputRef: ElementRef;
  @ViewChild(MissingFieldsComponent, { static: false })
  public missingFieldModal: MissingFieldsComponent;
  @ViewChild(EsShipmentDetailsDesktopComponent, { static: false })
  public esShipmentDetailsDesktopComponent: EsShipmentDetailsDesktopComponent;
  @ViewChild('newBLForm', { static: false }) public newBLForm: ElementRef;
  public entrySummary: EntrySummaryModel;
  public entrySummaryDate: string;
  public attachedDocuments: DocumentModel[] = [];
  public dutiesValue: EntrySummaryDutiesModel = null;
  public validationMessages$: Observable<ValidationMessageModel[]>;
  public isShowPaymentResult: boolean = false;
  public affiliateData: AffiliateData = { show: false };
  public paymentError: ICustomError;
  public deliveryOrders: DeliveryOrderModel[];
  public DEFAULT_CURRENCY_CODE = DEFAULT_CURRENCY_CODE;
  public isCanEdit: boolean = false;
  public isLoadingSaveBl: boolean;
  // public isCanUploadDocuments: boolean = false;
  public isOpenFiles: boolean = false;
  public company: CompanyModel;
  public companyPaymentType: enumCompanyPaymentType = enumCompanyPaymentType.INSTANT;
  public enumEntityModelCode: typeof enumEntityModelCode = enumEntityModelCode;
  public enumEntrySummarySubmissionType = enumEntrySummarySubmissionType;
  public isTablet: boolean = false;
  public isPrinting: boolean = false;
  public isEditMode: boolean = false;
  public pendingBills: BillItemPendingModel[] = [];
  public canceledBills: BillItemPendingModel[] = [];
  public cardNoticeMessagesLoaded: boolean = false;
  public deliveryOrdersInfo: DeliveryOrderModel[] = [];
  public currencies: CurrencyModel[] = [];
  public currencyRate = 1;
  public ratesMap: Map<string, CurrencyRateModel> = new Map<string, CurrencyRateModel>([
    [
      DEFAULT_CURRENCY_CODE,
      {
        code: DEFAULT_CURRENCY_CODE,
        rate: 1,
      } as CurrencyRateModel,
    ],
  ]);
  public currencyWarningRateModalData: {
    currency: string;
    exportDate: string;
  } = {
    currency: null,
    exportDate: null,
  };
  public openCurrencyWarningRateModal: boolean = false;
  public openOcrAllConfirmModal: boolean;
  public isOpenAddBillOfLadingModal: boolean = false;
  public isShowInvalidFileToast: boolean = false;

  public errorMessage: string = '';

  public blModalShipment: ShipmentModel;
  public blModalShipmentIndex: number;
  public blModalSkipOcrScanControl: UntypedFormControl;
  public blModalInputFiles: Map<number, File> = new Map();
  public blModalEntryDocs: DocumentModel[] = [];
  public blModalDocuments: DocumentModel[] = [];
  public blModalAdditionalDocumentCounter: number = 0;
  public blModalCurrentAdditionalDocumentShipmentId: number;
  public blModalCurrentAdditionalDocumentClicked: DocumentModel;
  public isOpenRemainingRequiredModal: boolean;

  public defaultBlModalDocs: enumDocumentType[] = [
    enumDocumentType.BILL_OF_LADING,
    enumDocumentType.INVOICE,
    enumDocumentType.ARRIVAL_NOTICE,
  ];
  public additionalBlModalDocsOptions: enumDocumentType[] = [
    enumDocumentType.INVOICE,
    enumDocumentType.ARRIVAL_NOTICE,
    enumDocumentType.PACKING_LIST,
    enumDocumentType.TRADE_CERTIFICATE,
    enumDocumentType.UNKNOWN,
  ];

  public fileControl: UntypedFormControl = new UntypedFormControl(null);
  public allowedFileMimes: string = '';
  public fileMimeIgnoreList: enumMimeType[] = [enumMimeType.XLS];
  public fileExtensionIgnoreList: enumFileExtension[] = [enumFileExtension.XLS];

  public isDesktop = false;
  public isEditInvoice = false;
  public currentEditedBillOfLadingId: number;
  public incotermsByType: IncoTermModel[] = [];
  public cbpLogMessages: ManagementEntityEventModel[] = [];
  public isInvoiceSaved: boolean;
  public isCreateBL: boolean;
  public newlyCreatedInvoicesIds: number[] = [];
  public newlyCreatedInvoicesFile: File;

  public createBLFormGroup: UntypedFormGroup;
  public isNewShipmentLoaded: boolean;
  public newShipment: ShipmentModel;
  public isOcrConfirmCtrls: UntypedFormArray;
  public isAllOcrConfirmCtrl: UntypedFormControl;

  public isESSynced: boolean;
  public isESJustLoaded: boolean;

  public allInvoiceNumbers: string[];

  public enumUserBetaFlag: typeof enumUserBetaFlag = enumUserBetaFlag;
  public enumMarineInsuranceEntityType: typeof enumMarineInsuranceEntityType = enumMarineInsuranceEntityType;
  public enumPaymentState: typeof enumPaymentState = enumPaymentState;
  public paidOrSentPaymentStates: enumPaymentState[] = [
    enumPaymentState.SUCCESS,
    enumPaymentState.PARTIALLY_REFUNDED,
    enumPaymentState.DELAYED,
  ];
  public enumMarineInsurancePendingPaymentAction: typeof enumMarineInsurancePendingPaymentAction = enumMarineInsurancePendingPaymentAction;
  public enumDocumentRootEntityType: typeof enumDocumentRootEntityType = enumDocumentRootEntityType;

  private incoterms: IncoTermModel[] = [];
  private invoiceFg: UntypedFormGroup;
  private entityFg: UntypedFormGroup;
  private fg: UntypedFormGroup;
  private currentShipment: ShipmentModel;
  private currentInvoice: InvoiceModel;
  private creatingInvoice: InvoiceModel;
  private hasTemporaryUploadedDoc: boolean;

  public onPoaSidebarRequest(docType: enumDocumentType): void {
    this.openPoaSidebar(docType);
  }

  private openPoaSidebar(docType?: enumDocumentType): void {
    const componentSidebarRef: ComponentRef<EsPoaSidebarComponent> = this.appService.openSidebarAsWindow(EsPoaSidebarComponent, {
      sidebarHeaderText: '',
      isMd: true,
    });

    const resolvedDocType = docType ?? enumDocumentType.POWER_OF_ATTORNEY;
    const headerTitle = 'DOCUMENT_TYPE.POWER_OF_ATTORNEY';

    componentSidebarRef.instance.company = this.company;
    componentSidebarRef.instance.entrySummary = this.entrySummary;
    componentSidebarRef.instance.headerTitle = headerTitle;
    componentSidebarRef.instance.shipmentId = this.entrySummary?.shipments?.[0]?.id;
    componentSidebarRef.instance.docType = resolvedDocType;

    componentSidebarRef.instance.close.pipe(takeUntil(this.destroy$)).subscribe(() => {
      this.appService.closeSidebarAsWindow();
    });

    componentSidebarRef.instance.saveSelectedPoa.pipe(takeUntil(this.destroy$)).subscribe((response) => {
      const { document: selectedDoc, docType: selectedDocType } = response || {};
      if (!selectedDoc || !this.entrySummary?.id) {
        this.appService.closeSidebarAsWindow();
        return;
      }

      this.documentsService
        .copyDocToEntry(this.entrySummary.id, selectedDoc.id, selectedDoc.fileName, selectedDocType)
        .pipe(
          switchMap(() => this.entrySummaryStateService.loadEntrySummary(this.entrySummary.referenceNumber)),
          tap((updatedEntry) => {
            this.entrySummaryStateService.updateCurrentEntrySummary(updatedEntry);
          }),
          switchMap(() => this.entrySummaryStateService.updateDocuments(false)),
          take(1),
        )
        .subscribe({
          next: () => {
            this.changeDetectorRef.markForCheck();
            this.appService.closeSidebarAsWindow();
          },
          error: () => {
            this.appService.closeSidebarAsWindow();
          },
        });
    });
  }
  private isAllLoadedAndChecked: boolean;

  private componentSidebarRef: ComponentRef<any>;

  private currentWizardIndex: number;
  private stepsWizardOrder: Array<{
    component: Type<EsInvoiceFormDesktopComponent | EsEntityUnifyDefaultDesktopComponent | EsLineItemFormDesktopComponent>;
    dataGeneral?: { invoice: InvoiceModel; shipment: ShipmentModel };
    dataLines?: { invoiceId: number; shipmentId: number; itemId?: number };
  }> = [];

  private loadInsuranceDocuments$: ReplaySubject<void> = new ReplaySubject<void>();
  private loadedES$: ReplaySubject<boolean> = new ReplaySubject<boolean>();
  private loadedPendingBills$: ReplaySubject<boolean> = new ReplaySubject<boolean>();
  private loadedDuties$: ReplaySubject<boolean> = new ReplaySubject<boolean>();
  private wizardDestroy$: Subject<void> = new Subject<void>();
  private destroy$: ReplaySubject<boolean> = new ReplaySubject();

  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private entrySummaryStateService: EntrySummaryStateService,
    private companiesService: CompaniesService,
    private ga: GoogleAnalyticsService,
    private genericService: GenericService,
    private changeDetectorRef: ChangeDetectorRef,
    private deliveryOrdersRepository: DeliveryOrdersRepository,
    private deliveriesOrderService: DeliveriesOrderService,
    private translateService: TranslateService,
    private invoicesRepository: InvoicesRepository,
    private codeRepository: CodeRepository,
    private currenciesRepository: CurrenciesRepository,
    private fh: FormHelper,
    private location: Location,
    private customDatePipe: CustomDateByUtcPipe,
    private documentsService: DocumentsService,
    private entrySummaryMessagesRepository: EntrySummaryMessagesRepository,
    private entrySummaryRepository: EntrySummaryRepository,
    private esStepsService: EsStepsService,
    private esCreateBlFormService: EsCreateBlFormService,
    private appLayoutService: AppLayoutService,
    private marineInsuranceSideformService: MarineInsuranceSideformService,
    private marineInsuranceState: MarineInsuranceState,
    private paymentState: PaymentState,
    private appService: AppService,
    private esValidationsHelperService: EsValidationsHelperService,
    private htsLinesReviewService: HtsLinesReviewService,
  ) {
    this.validationMessages$ = this.entrySummaryStateService.validationMessages$;
    this.deliveriesOrderService.clear();
  }

  public ngOnInit(): void {
    this.marineInsuranceState.resetData();
    this.allowedFileMimes = ALLOWED_FILE_MIMES.filter((fm) => !this.fileMimeIgnoreList.includes(fm)).join(',');
    this.blModalSkipOcrScanControl = new UntypedFormControl(false);
    this.blModalAdditionalDocumentCounter = 0;

    this.appLayoutService.internalNotesUpdated$
      .pipe(
        skip(1),
        switchMap(() => this.entrySummaryStateService.loadEntrySummary(this.entrySummary.referenceNumber)),
        takeUntil(this.destroy$),
      )
      .subscribe((entrySummary: EntrySummaryModel) => {
        this.entrySummary = { ...entrySummary };
        this.changeDetectorRef.markForCheck();
      });

    this.genericService.isTablet$.pipe(takeUntil(this.destroy$)).subscribe((isTablet) => {
      this.isTablet = isTablet;
    });
    this.genericService.isPrinting$.pipe(takeUntil(this.destroy$)).subscribe((isPrinting) => {
      this.isPrinting = isPrinting;
    });
    this.company = this.companiesService.getCompany();
    this.companyPaymentType = this.company.customsPaymentType;

    this.fg = this.fh.createEntryShipmentFormGroup(this.company?.authorizationAgreementAccepted && !!this.company?.payerUnitNumber);
    this.createBLFormGroup = this.esCreateBlFormService.createForm();
    this.invoiceFg = new UntypedFormGroup({
      manufacturerId: new UntypedFormControl(null),
      manufacturer: this.fh.createEntityModelFormGroup(),
      invoiceNumber: new UntypedFormControl(null, [Validators.required]),
      currency: new UntypedFormControl(DEFAULT_CURRENCY_CODE, [Validators.required]),
      incoTermsCode: new UntypedFormControl(null),
      countryOfExportId: new UntypedFormControl(null, [Validators.required]),
      countryOfExport: new UntypedFormControl(null),
    });
    this.invoiceFg
      .get('currency')
      .valueChanges.pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.checkCurrency();
      });
    this.isOcrConfirmCtrls = new UntypedFormArray([]);
    this.isAllOcrConfirmCtrl = new UntypedFormControl(false, [Validators.requiredTrue]);
    this.isAllOcrConfirmCtrl.valueChanges.pipe(takeUntil(this.destroy$)).subscribe((value: boolean) => {
      this.isOcrConfirmCtrls.controls.forEach((fc) => {
        if (value) {
          fc.patchValue({ ...fc.value, confirmed: true }, { emitValue: false });
          this.changeDetectorRef.markForCheck();
        }
      });
    });

    combineLatest([this.loadedES$, this.loadedPendingBills$, this.loadedDuties$])
      .pipe(
        filter(
          ([loadedES, loadedPendingBills, loadedDuties]: [boolean, boolean, boolean]) =>
            !!loadedES && !!loadedPendingBills && !!loadedDuties && !this.isAllLoadedAndChecked,
        ),
      )
      .subscribe(() => {
        this.isAllLoadedAndChecked = true;
        this.listenPaymentActions();
      });

    this.entrySummaryStateService.entrySummary$.pipe(takeUntil(this.destroy$)).subscribe((entrySummary) => {
      this.isCanEdit = EntrySummaryActionsHelper.isCanEdit(entrySummary);
      // this.isCanUploadDocuments = EntrySummaryActionsHelper.isCanUploadDocuments(entrySummary);

      this.entrySummary = entrySummary;
      this.loadedES$.next(true);
      if (this.entrySummary && this.entrySummary.id) {
        this.isOpenFiles = this.entrySummaryStateService.isShowDocuments(this.entrySummary) ? true : this.isOpenFiles;
        this.getDuties();
        this.getCurrencies();
        this.loadInvoiceIncoterms();
        this.getCbpLogMessages();
        this.getInsuranceState(this.entrySummaryDate !== this.entrySummary.shipments[0]?.exportDate && this.isESJustLoaded);
        this.fg.patchValue(this.entrySummary);
        this.updateTruckValidation();
        this.isESSynced = this.entrySummaryStateService.isESSynced(this.entrySummary.state);

        this.entrySummaryDate = this.entrySummary.shipments[0]?.exportDate;

        if (
          !this.isESJustLoaded &&
          !this.isCreatingInvoice &&
          this.entrySummary.shipments.every((shipment: ShipmentModel) => this.isInvoiceValid(shipment))
        ) {
          // case when we just loaded the page
          this.isInvoiceSaved = true;
        }
        this.isESJustLoaded = true;
      }

      this.allInvoiceNumbers = this.getInvoiceNumbers();

      this.isOcrConfirmCtrls.clear({ emitEvent: false });
      (this.entrySummary?.shipments || []).forEach((shipment: ShipmentModel) => {
        const isChecked: boolean = this.checkIsOcrConfirmed(shipment);
        this.isOcrConfirmCtrls.push(
          new UntypedFormGroup({
            id: new UntypedFormControl(shipment.id),
            confirmed: new UntypedFormControl(isChecked, {
              validators: this.checkIsShowOcrConfirm(shipment) ? [Validators.requiredTrue] : [],
            }),
          }),
        );
      });
    });

    //#11872
    if (this.isHasEsOcrRedesign) {
      this.entrySummaryStateService.invoiceSaved$.pipe(takeUntil(this.destroy$)).subscribe((isSaved) => {
        if (!isSaved) {
          return;
        }
        this.onInvoiceSaved();
      });

      this.entrySummaryStateService.invoiceCleared$
        .pipe(
          switchMap((isCleared) => {
            if (!isCleared) {
              return of(null);
            }

            return this.entrySummaryStateService.loadEntrySummary(this.entrySummary.referenceNumber);
          }),
          takeUntil(this.destroy$),
        )
        .subscribe((entry: EntrySummaryModel | null) => {
          if (!entry) {
            return;
          }

          this.entrySummaryStateService.updateCurrentEntrySummary(entry);
          this.invoiceFg.reset({ currency: DEFAULT_CURRENCY_CODE });
          this.isInvoiceSaved = false;
          this.isEditInvoice = false;
          this.editInvoiceChange.emit(this.isEditInvoice);
          this.currentEditedBillOfLadingId = null;
          this.changeDetectorRef.detectChanges();
        });
    }

    this.entrySummaryStateService.pendingBills$.pipe(takeUntil(this.destroy$)).subscribe((pendingBills) => {
      this.pendingBills = pendingBills;
      this.loadedPendingBills$.next(true);
    });
    this.entrySummaryStateService.canceledBills$.pipe(takeUntil(this.destroy$)).subscribe((canceledBills) => {
      this.canceledBills = canceledBills;
    });

    this.entrySummaryStateService.entryDocuments$.pipe(takeUntil(this.destroy$)).subscribe((documents) => {
      this.attachedDocuments = documents;
      if (this.attachedDocuments.length) {
        this.isOpenFiles = true;
      }
      if (documents.some((doc) => doc.type === enumDocumentType.DELIVERY_ORDER)) {
        this.getDeliveryOrderInfo();
      }
    });

    combineLatest(this.genericService.isDesktop$, this.entrySummaryStateService.entrySummary$)
      .pipe(distinctUntilChanged(), takeUntil(this.destroy$))
      .subscribe(([isDesktop, entrySummary]) => {
        this.isDesktop =
          isDesktop &&
          this.companiesService
            .getUser()
            ?.betaFlags?.map((flag) => flag.tag)
            .includes(
              entrySummary.submissionType === enumEntrySummarySubmissionType.BROKER_ASSISTED
                ? enumUserBetaFlag.esBrokerDesktop
                : enumUserBetaFlag.esSelfDesktop,
            );
      });

    this.router.events
      .pipe(
        filter((event) => event instanceof NavigationEnd),
        takeUntil(this.destroy$),
      )
      .subscribe((route) => {
        this.esStepsService.checkAndRedirectESToSummary(this.entrySummary);
      });

    this.createBLFormGroup.addControl('seller', this.fh.createEntityModelFormGroup(enumEntityModelCode.SELLER));
    this.fh.updateEntityValidation(true, this.createBLFormGroup.get('seller'));

    this.updateTruckValidation();
    this.loadInsuranceDocuments$
      .pipe(
        withLatestFrom(this.marineInsuranceState.currentInsurance),
        switchMap(([_, currentInsurance]: [void, MarineInsuranceModel]) =>
          this.entrySummaryStateService.getInsuranceDocuments(currentInsurance.id),
        ),
        takeUntil(this.destroy$),
      )
      .subscribe(
        (docs: DocumentModel) => {
          this.marineInsuranceState.saveDocsData(docs);
          this.marineInsuranceState.saveIsLoadingData(false);
        },
        () => this.marineInsuranceState.saveIsLoadingData(false),
      );

    this.isOcrConfirmCtrls.valueChanges
      .pipe(
        switchMap((data: [{ id: number; confirmed: boolean }]) => {
          const apiCalls: Array<Observable<number>> = [];
          const invoiceIds: number[] = [];
          data.forEach((formValue: { id: number; confirmed: boolean }) => {
            if (formValue.confirmed) {
              invoiceIds.push(
                ...this.entrySummary?.shipments
                  ?.find((shipment: ShipmentModel) => shipment.id === formValue.id)
                  ?.invoices?.filter((invoice: InvoiceModel) => invoice.ocrStatus !== enumOcrStatus.REVIEWED)
                  .map((invoice: InvoiceModel) => invoice.id),
              );
            }
          });

          (invoiceIds || []).forEach((id: number) => {
            apiCalls.push(this.entrySummaryStateService.setOcrStatus(id));
          });

          return apiCalls.length ? forkJoin(...apiCalls) : of([]);
        }),
        switchMap((data: number[]) =>
          data.length ? this.entrySummaryStateService.loadEntrySummary(this.entrySummary.referenceNumber) : of(null),
        ),
        takeUntil(this.destroy$),
      )
      .subscribe((entry: EntrySummaryModel) => {
        if (!!entry) {
          this.entrySummaryStateService.updateCurrentEntrySummary(entry);
        }
      });

    this.entrySummaryStateService.wizardStepNext$
      .pipe(
        tap(() => this.wizardDestroy$.next()),
        takeUntil(this.destroy$),
      )
      .subscribe(() => {
        this.currentWizardIndex++;
        this.handleWizardNextStep();
      });
    this.entrySummaryStateService.wizardStepPrev$
      .pipe(
        tap(() => this.wizardDestroy$.next()),
        takeUntil(this.destroy$),
      )
      .subscribe(() => {
        this.currentWizardIndex--;
        this.handleWizardPrevStep();
      });

    const queryParamMap = this.route.snapshot.queryParamMap['params'];
    const isOpenMarineInsuranceQuoteForm = queryParamMap ? queryParamMap['openMarineInsuranceQuoteForm'] : null;
    // #13417 — insurance is offered only after the entry is sent
    if (
      isOpenMarineInsuranceQuoteForm === 'true' &&
      this.isEsSent &&
      (!this.entrySummary.insuranceStatus || this.entrySummary.insuranceStatus === enumMarineInsuranceStatusType.NOT_INSURED)
    ) {
      // Wait for insurance data to be loaded before opening the sidebar
      this.isInsuranceAvailable.pipe(take(2), takeUntil(this.destroy$)).subscribe(() => {
        this.removeQueryParams(['openMarineInsuranceQuoteForm']); //Has to run before opening sidebar due to router event listener closing sidebar on change
        // Use setTimeout to avoid ExpressionChangedAfterItHasBeenCheckedError
        setTimeout(() => {
          this.openMarineInsuranceQuoteForm();
          this.changeDetectorRef.markForCheck();
        }, 0);
      });
    }

    const isOpenPoaSidebar = queryParamMap ? queryParamMap['openPoaSidebar'] : null;
    if (isOpenPoaSidebar === 'true') {
      this.removeQueryParams(['openPoaSidebar']);
      this.openPoaSidebar();
      this.changeDetectorRef.markForCheck();
    }
  }

  public ngOnDestroy(): void {
    this.entrySummaryStateService.currentOCRState$.next(null);
    this.paymentState.resetPaymentState();
    this.marineInsuranceState.resetData();

    this.destroy$.next(true);
    this.destroy$.complete();
  }

  public get isShowInfo(): boolean {
    return (
      this.entrySummary &&
      this.entrySummary.state === enumEntrySummaryState.NOT_SENT &&
      this.entrySummary.paymentState !== enumPaymentState.PENDING
    );
  }

  public get isShowDocumentsButton(): boolean {
    return this.isShowInfo && this.entrySummary && this.entrySummary.submissionType !== enumEntrySummarySubmissionType.BROKER_ASSISTED;
  }

  public get showChatWithBroker(): boolean {
    if (this.entrySummary.submissionType !== enumEntrySummarySubmissionType.BROKER_ASSISTED) {
      return false;
    }
    if (
      [
        enumEntrySummaryState.BROKER_PROCESSING_FAILED,
        enumEntrySummaryState.NOT_VALID_PROVIDER,
        enumEntrySummaryState.UNRECOVERABLE,
        enumEntrySummaryState.REJECTED,
        enumEntrySummaryState.NO_BILL_ON_FILE,
      ].includes(this.entrySummary.state) ||
      this.entrySummary.hasNewBrokerMessages
    ) {
      return true;
    }

    return false;
  }

  public get isEditModeOn(): boolean {
    return this.isShowInfo ? this.isCanEdit : this.isCanEdit && this.isEditMode;
  }

  /** #13261: Truck mode in the details sidebar follows the same beta flag as the create page. */
  public get isTruckModeAvailable(): boolean {
    return this.companiesService.isEsTruckModeAvailable;
  }

  public get isCanAddBL(): boolean {
    return !this.isCreating && !this.creatingInvoiceBLId && !this.isSidebar;
  }

  public afterMessagesLoaded() {
    this.cardNoticeMessagesLoaded = true;
  }

  public onPrint(isSkipCardNoticeMessagesLoaded?: boolean) {
    this.cardNoticeMessagesLoaded = false;
    this.genericService.isPrinting$.next(true);
    const isFinished: ReplaySubject<boolean> = new ReplaySubject<boolean>();
    interval(500)
      .pipe(takeUntil(isFinished))
      .subscribe(() => {
        if (this.cardNoticeMessagesLoaded || isSkipCardNoticeMessagesLoaded) {
          window.print();
          isFinished.next(true);
          isFinished.complete();
        }
      });
  }

  public addToCard() {
    this.ga.addToCart(this.entrySummary.referenceNumber, this.pendingBills);
  }

  public cancelPayment() {
    this.hidePaymentResult();
  }

  public openResult(affiliateData: AffiliateData, paymentError?: ICustomError) {
    this.affiliateData = { ...affiliateData, orderId: this.entrySummary.referenceNumber };
    this.deliveryOrders = [];
    if (paymentError) {
      this.paymentError = paymentError;
      this.isShowPaymentResult = true;
      this.changeDetectorRef.detectChanges();
      return;
    }
    this.deliveriesOrderService.startNew();
    this.deliveriesOrderService.currentDeliveryOrderList.pipe(take(2)).subscribe((deliveryOrders) => {
      this.deliveryOrders = deliveryOrders;
      this.paymentError = paymentError;
      this.isShowPaymentResult = true;
      this.changeDetectorRef.detectChanges();
    });
  }

  public hidePaymentResult() {
    this.paymentError = undefined;
    this.isShowPaymentResult = false;
  }

  public onDocumentStateUpdate(event: IEsDocumentStateUpdate) {
    this.entrySummaryStateService.updateDocumentState(event?.documentId, event?.state);
  }

  public onUploadCompanyFile(): (document: DocumentCreateModel, file: File, uploadToCompany: boolean) => Observable<any> {
    return this.uploadPoaOrPunDocument.bind(this);
  }

  public onUploadFile(): (document: DocumentCreateModel, file: File) => Observable<any> {
    return this.uploadDocument.bind(this);
  }

  public onRemoveFile(): (documentId: number) => Observable<any> {
    return this.entrySummaryStateService.removeDocument.bind(this.entrySummaryStateService);
  }

  public onRemoveInvoice(shipmentId: number, invoiceId: number) {
    this.entrySummaryStateService
      .removeInvoice(shipmentId, invoiceId)
      .pipe(
        switchMap(() => {
          return this.entrySummaryStateService.loadEntrySummary(this.entrySummary.referenceNumber).pipe(take(1));
        }),
        takeUntil(this.destroy$),
      )
      .subscribe((entry: EntrySummaryModel) => {
        this.entrySummaryStateService.updateCurrentEntrySummary(entry);
        this.invoiceFg.reset({ currency: DEFAULT_CURRENCY_CODE });
        this.isInvoiceSaved = true;
        this.isEditInvoice = false;
        this.editInvoiceChange.emit(this.isEditInvoice);
        this.currentEditedBillOfLadingId = null;
      });
  }

  public onRemoveShipment(shipmentId: number) {
    this.entrySummaryStateService.removeShipment(shipmentId);
  }

  public setIsOpenAddBillOfLadingModal(value: boolean) {
    this.isOpenAddBillOfLadingModal = value;
  }

  public onAddBlModalDocument(data: { file: File; doc: DocumentModel; shipmentId: number }): void {
    const { file, doc } = data;

    const attachedDocuments = [...this.blModalDocuments];
    const existingDocumentIndex = attachedDocuments.findIndex((d) => d.id === doc.id);

    const addedDocumentFields = {
      state: enumDocumentState.CREATED,
      fileName: file.name,
      createdDate: new Date().toDateString(),
    };

    if (existingDocumentIndex >= 0) {
      const existingDocument = attachedDocuments[existingDocumentIndex];
      attachedDocuments[existingDocumentIndex] = {
        ...existingDocument,
        ...addedDocumentFields,
      };
    } else {
      attachedDocuments.push({
        ...doc,
        ...addedDocumentFields,
      });
    }

    this.blModalInputFiles.set(doc.id, file);
    this.blModalDocuments = [...attachedDocuments];

    this.changeDetectorRef.detectChanges();
  }

  public onAddOtherBlModalDocument(data: { type: enumDocumentType; shipmentId: number }): void {
    this.setIsShowInvalidFileToast(false);
    const { type, shipmentId } = data;
    this.blModalCurrentAdditionalDocumentClicked = {
      id: this.blModalAdditionalDocumentCounter,
      rootEntityId: this.entrySummary.id,
      rootEntityType: enumDocumentRootEntityType.ENTRY_SUMMARY,
      relatedEntityId: shipmentId,
      relatedEntityType: enumDocumentRelatedEntityType.SHIPMENT,
      url: null,
      createdDate: null,
      fileName: null,
      type,
      state: null,
    };
    this.blModalCurrentAdditionalDocumentShipmentId = shipmentId;
    this.additionalDocumentsInputRef.nativeElement.click();
    this.blModalAdditionalDocumentCounter++;
  }

  public onUploadNewBlModalDocument(data: { file: File; type: enumDocumentType; shipmentId: number }): void {
    this.setIsShowInvalidFileToast(false);
    const { file, type, shipmentId } = data;

    const doc: DocumentModel = {
      id: this.blModalAdditionalDocumentCounter++,
      rootEntityId: this.entrySummary.id,
      rootEntityType: enumDocumentRootEntityType.ENTRY_SUMMARY,
      relatedEntityId: shipmentId,
      relatedEntityType: enumDocumentRelatedEntityType.SHIPMENT,
      url: null,
      createdDate: null,
      fileName: null,
      type,
      state: null,
    };

    this.onAddBlModalDocument({ file, doc, shipmentId });
  }

  public onAddOtherBlDocumentChange(event: Event): void {
    this.isLoading = true;
    const target: HTMLInputElement = event.target as HTMLInputElement;
    const files: FileList = target.files;
    if (files && files.length) {
      const file = files[0];
      this.fileControl.patchValue(null);
      this.fileControl.markAsUntouched();
      this.fileControl.patchValue(file);
      this.fileControl.markAsTouched();
      this.updateInputErrors(file);

      if (this.fileControl.valid) {
        this.onAddBlModalDocument({
          file: files[0],
          doc: this.blModalCurrentAdditionalDocumentClicked,
          shipmentId: this.blModalCurrentAdditionalDocumentShipmentId,
        });
      } else {
        this.setIsShowInvalidFileToast(true);
      }
    }
    this.additionalDocumentsInputRef.nativeElement.value = '';
    this.changeDetectorRef.detectChanges();
  }

  public onBlModalInputClick() {
    this.setIsShowInvalidFileToast(false);
  }

  public onReplaceBlModalDoc() {
    this.setIsShowInvalidFileToast(false);
    this.additionalDocumentsInputRef.nativeElement.click();
  }

  public onRemoveBlModalDocument(data: { doc: DocumentModel; shipmentId: number }): void {
    const { doc } = data;

    const document = this.blModalDocuments.find((d) => d.id === doc.id);
    const documentIndex = this.blModalDocuments.findIndex((d) => d.id === doc.id);
    if (documentIndex == -1 || !document) {
      return;
    }

    this.blModalInputFiles.delete(doc.id);

    const isFromDefaultDocs = document.id < 0;

    if (isFromDefaultDocs) {
      this.blModalDocuments[documentIndex] = {
        id: document.id,
        rootEntityId: this.entrySummary.id,
        rootEntityType: enumDocumentRootEntityType.ENTRY_SUMMARY,
        relatedEntityId: document.relatedEntityId,
        relatedEntityType: enumDocumentRelatedEntityType.SHIPMENT,
        url: null,
        createdDate: null,
        fileName: null,
        type: document.type,
        state: null,
      };
    } else {
      this.blModalDocuments = this.blModalDocuments.filter((d) => d.id !== doc.id);
    }

    this.changeDetectorRef.markForCheck();
  }

  public updateBlModalDocumentState(data: { docId: number; state: enumDocumentState }): void {
    const { docId, state } = data;
    const document = this.blModalDocuments.find((doc) => doc.id === docId);
    if (!document) {
      return;
    }

    document.state = state;
  }

  public onSaveBlModal(skipCheck: boolean = false) {
    if (skipCheck) {
      this.setIsOpenRemainingRequiredModal(false);
    }

    if (!!this.numberOfBlsWitMissingDocuments && !skipCheck) {
      this.setIsOpenRemainingRequiredModal(true);
      return;
    }

    this.isLoadingSaveBl = true;

    this.setIsOpenAddBillOfLadingModal(false);

    this.uploadBlModalDocuments()
      .pipe(takeUntil(this.destroy$))
      .subscribe((docs) => {
        this.entrySummaryStateService
          .updateEntrySummary({
            ...this.entrySummary,
          })
          .pipe(take(1))
          .subscribe((entry) => {
            this.entrySummaryStateService.updateCurrentEntrySummary(entry);
            if (this.blModalSkipOcrScanControl.value) {
              this.router.navigate(['entry-summary', entry.referenceNumber]);
            } else {
              this.router.navigate(['entry-summary', entry.referenceNumber, 'documents'], {
                queryParams: { isOcrScanOnStart: true },
              });
            }
            this.isLoadingSaveBl = false;
            this.blModalSkipOcrScanControl.patchValue(false);
            this.entrySummaryStateService.entryBlDocumentsUploaded$.next(docs);
            this.changeDetectorRef.detectChanges();
          });
      });
  }

  public setIsOpenRemainingRequiredModal(value: boolean) {
    this.isOpenRemainingRequiredModal = value;
    this.changeDetectorRef.markForCheck();
  }

  public blMissingRequiredDocumentNames(): string {
    if (!this.blModalDocuments || !this.blModalShipment) {
      return '';
    }

    const requiredDocuments: { type: enumDocumentType; text: string }[] = [
      {
        type: enumDocumentType.INVOICE,
        text: 'LABELS.COMMERCIAL_INVOICE',
      },
    ];

    if (this.isBillOfLadingRequired) {
      requiredDocuments.push({
        type: enumDocumentType.BILL_OF_LADING,
        text: 'LABELS.BILL_OF_LADING',
      });
    }

    const missingRequiredDocumentNames: string[] = [];
    const missingRequiredDocumentTypes: enumDocumentType[] = this.missingBlModalRequiredDocuments.get(this.blModalShipment.id);

    missingRequiredDocumentTypes.forEach((type) => {
      const currentRequiredDocument = requiredDocuments.find((rd) => rd.type === type);
      missingRequiredDocumentNames.push(this.translateService.instant(currentRequiredDocument?.text));
    });

    return missingRequiredDocumentNames.join(', ');
  }

  public editShipmentData(validateAfterInit: boolean = false) {
    this.router.navigate(['entry-summary', this.entrySummary.referenceNumber, 'shipment-data'], this.getNavExtras(validateAfterInit));
  }

  public editBond(validateAfterInit: boolean = false) {
    this.router.navigate(['entry-summary', this.entrySummary.referenceNumber, 'customs-bond'], this.getNavExtras(validateAfterInit));
  }

  public editImporter(validateAfterInit: boolean = false) {
    this.router.navigate(['entry-summary', this.entrySummary.referenceNumber, 'importer-info'], this.getNavExtras(validateAfterInit));
  }

  public editShipment(shipmentId?: number, validateAfterInit: boolean = false) {
    if (this.isDesktop) {
      this.isCreateBL = !this.isHasEsOcrRedesign;
      this.esStepsService.isCreatingBL = !this.isHasEsOcrRedesign;

      // #11215 - setting create bl form to a new default one
      this.createBLFormGroup = this.esCreateBlFormService.createForm();
      this.createBLFormGroup.addControl('seller', this.fh.createEntityModelFormGroup(enumEntityModelCode.SELLER));
      this.fh.updateEntityValidation(true, this.createBLFormGroup.get('seller'));

      this.entrySummaryStateService
        .createShipment(this.createBLFormGroup.value, null, true)
        .pipe(take(1))
        .subscribe((shipment) => {
          this.newShipment = shipment;
          this.invoiceFg.get('currency').setValue(this.DEFAULT_CURRENCY_CODE);

          this.esCreateBlFormService.setData(this.entrySummary, shipment, this.isCanEdit);
          this.esCreateBlFormService.updateShipmentForm(this.entrySummary, false, shipment?.seller);
          this.isNewShipmentLoaded = !this.isHasEsOcrRedesign;

          // #9121
          this.marineInsuranceState.saveData(null, true, false);

          if (this.isDesktop) {
            if (this.isHasEsOcrRedesign) {
              this.blModalShipment = shipment;
              this.blModalShipmentIndex = this.entrySummary?.shipments.length;
              this.initializeBlModalDocuments(shipment?.id);
              this.setIsOpenAddBillOfLadingModal(true);
            } else {
              this.router.navigate(['entry-summary', this.entrySummary.referenceNumber], {
                queryParams: { [IS_CREATING_ES_INVOICE_KEY]: shipment?.id },
              });
            }
          }

          this.changeDetectorRef.markForCheck();
          // wait & scroll to element
          setTimeout(() => this.newBLForm.nativeElement.scrollIntoView(), 500);
        });
    } else {
      this.router.navigate(
        ['entry-summary', this.entrySummary.referenceNumber, 'bill-of-lading', shipmentId ? shipmentId : ''],
        shipmentId ? this.getNavExtras(validateAfterInit) : { queryParams: { validateAfterInit } },
      );
    }
  }

  public editShipmentConsignee(shipmentId: number, validateAfterInit: boolean = false) {
    this.router.navigate(
      ['entry-summary', this.entrySummary.referenceNumber, 'bill-of-lading', shipmentId, 'consignee-info'],
      this.getNavExtras(validateAfterInit),
    );
  }

  public editShipmentBuyer(shipmentId: number, validateAfterInit: boolean = false) {
    this.router.navigate(
      ['entry-summary', this.entrySummary.referenceNumber, 'bill-of-lading', shipmentId, 'buyer-info'],
      this.getNavExtras(validateAfterInit),
    );
  }

  public editSupplier(shipmentId: number) {
    this.router.navigate(
      ['entry-summary', this.entrySummary.referenceNumber, 'bill-of-lading', shipmentId, 'supplier-info'],
      this.getNavExtras(),
    );
  }

  public editInvoices(shipmentId: number) {
    this.router.navigate(
      ['entry-summary', this.entrySummary.referenceNumber, 'bill-of-lading', shipmentId, 'invoice-summary'],
      this.getNavExtras(),
    );
  }

  public addInvoice(shipmentId: number, validateAfterInit: boolean = false) {
    this.router.navigate(
      ['entry-summary', this.entrySummary.referenceNumber, 'bill-of-lading', shipmentId, 'invoice'],
      this.getNavExtras(validateAfterInit),
    );
  }

  public editInvoice(shipmentId: number, invoiceId: number, validateAfterInit: boolean = false) {
    this.router.navigate(
      ['entry-summary', this.entrySummary.referenceNumber, 'bill-of-lading', shipmentId, 'invoice', invoiceId],
      this.getNavExtras(validateAfterInit),
    );
  }

  public addLine(shipmentId: number, invoiceId: number, validateAfterInit: boolean = false) {
    this.router.navigate(
      ['entry-summary', this.entrySummary.referenceNumber, 'bill-of-lading', shipmentId, 'invoice', invoiceId, 'item'],
      this.getNavExtras(validateAfterInit),
    );
  }

  public editLine(shipmentId: number, invoiceId: number, itemId: number, validateAfterInit: boolean = false) {
    this.router.navigate(
      ['entry-summary', this.entrySummary.referenceNumber, 'bill-of-lading', shipmentId, 'invoice', invoiceId, 'item', itemId],
      this.getNavExtras(validateAfterInit),
    );
  }

  public goToFirstErrorPage(validationData: IEsValidationData) {
    const { editPageType } = validationData;

    switch (editPageType) {
      case enumEsEditPageType.ATTACHED_DOCUMENTS:
        this.scrollTop();
        break;

      case enumEsEditPageType.TRANSPORTATION_MODE:
        this.router.navigate(['entry-summary', this.entrySummary.referenceNumber, 'type'], this.getNavExtras(true));
        break;

      case enumEsEditPageType.IMPORTER:
        this.editImporter(true);
        break;

      case enumEsEditPageType.BOND:
        this.editBond(true);
        break;

      case enumEsEditPageType.SHIPMENT_DATA:
        this.editShipmentData(true);
        break;

      case enumEsEditPageType.BILL_OF_LADING:
        if (validationData.data.shipmentId) {
          this.editShipment(validationData.data.shipmentId, true);
        } else {
          this.editShipment(null, true);
        }
        break;

      case enumEsEditPageType.SHIPMENT_SELLER:
        this.editSupplier(validationData.data.shipmentId);
        break;

      case enumEsEditPageType.SHIPMENT_CONSIGNEE:
        this.editShipmentConsignee(validationData.data.shipmentId, true);
        break;

      case enumEsEditPageType.SHIPMENT_INVOICE:
        if (validationData.data.invoiceId) {
          this.editInvoice(validationData.data.shipmentId, validationData.data.invoiceId, true);
        } else {
          this.addInvoice(validationData.data.shipmentId, true);
        }
        break;

      case enumEsEditPageType.SHIPMENT_INVOICE_LINE:
        if (validationData.data.invoiceId && validationData.data.lineId) {
          this.editLine(validationData.data.shipmentId, validationData.data.invoiceId, validationData.data.lineId, true);
        } else {
          this.addLine(validationData.data.shipmentId, validationData.data.invoiceId, true);
        }
        break;

      case enumEsEditPageType.HTS_VALIDATION:
        // #13384: the gate is cleared in the classification assistant's Validate view, same as the Finalize button
        if (this.companiesService.hasUserBetaFlag(enumUserBetaFlag.htsClassificationAssistant)) {
          this.htsLinesReviewService.open({ isReadonly: !this.isEditModeOn, panelComponent: EsHtsLinePanelComponent });
        }
        break;

      default:
        break;
    }
  }

  public goToDashboard() {
    this.router.navigate(['dashboard']);
  }

  public goToBrokerChat() {
    this.router.navigate(['entry-summary', this.entrySummary.referenceNumber, 'custom-broker']);
  }

  public initCompanyEntity() {
    if (!this.company) {
      return;
    }

    const { address, dateOfBirth, identifier, identifierQualifier } = this.company;
    const name = this.company.businessName || `${this.company.firstName} ${this.company.lastName}`;
    return new EntityModel({
      address: { ...address, id: 0 },
      addressId: null,
      dateOfBirth: dateOfBirth ? dateOfBirth.toString() : null,
      name,
      entityIdentifier: identifier,
      entityIdentifierQualifier: identifierQualifier,
      id: this.entrySummary.importer?.id,
      entityCode: enumEntityModelCode.IMPORTER,
    });
  }

  public onEditDetails(formKey: string): void {
    this.componentSidebarRef = this.appService.openSidebar(EsEditShipmentDesktopComponent, {
      sidebarHeaderText: 'LABELS.EDIT_SHIPMENT_DETAILS',
    });

    this.componentSidebarRef.instance.entrySummary = this.entrySummary;
    this.componentSidebarRef.instance[formKey] = true;
    this.componentSidebarRef.instance.isTruckModeAvailable = this.isTruckModeAvailable;

    this.componentSidebarRef.instance.dataSaved.pipe(takeUntil(this.destroy$)).subscribe(() => {
      // marine insurance is not offered for Rail and Truck (#13261 adds Truck)
      if ([enumTransportationMode.RAIL, enumTransportationMode.TRUCK].includes(this.entrySummary.transportationMode)) {
        this.marineInsuranceState.saveData(null, false, true);
      }
      this.appService.closeSidebar();
    });
  }

  public onEditShipmentDetails(): void {
    this.componentSidebarRef = this.appService.openSidebar(EsBondsDesktopComponent, {
      sidebarHeaderText: 'LABELS.SHIPMENT_DETAILS',
    });

    this.componentSidebarRef.instance.entrySummary = this.entrySummary;
    this.componentSidebarRef.instance.company = this.company;

    this.componentSidebarRef.instance.dataSaved
      .pipe(
        takeUntil(this.destroy$),
        switchMap((data: EntrySummaryModel) => {
          return this.entrySummaryStateService.updateEntrySummary(data);
        }),
      )
      .subscribe((entry) => {
        this.entrySummaryStateService.updateCurrentEntrySummary(entry);

        this.appService.closeSidebar();
        this.changeDetectorRef.detectChanges();
      });
  }

  public onEditDutiesType(): void {
    this.componentSidebarRef = this.appService.openSidebar(EsDutiesTypeDesktopComponent, {
      sidebarHeaderText: 'LABELS.DUTIES_PAYMENT_TYPE',
    });

    this.componentSidebarRef.instance.entrySummary = this.entrySummary;
    this.componentSidebarRef.instance.company = this.company;

    this.componentSidebarRef.instance.dataSaved
      .pipe(
        takeUntil(this.destroy$),
        switchMap((data: EntrySummaryModel) => {
          if (
            this.entrySummary.submissionType === enumEntrySummarySubmissionType.MANUAL &&
            data.submissionType === enumEntrySummarySubmissionType.BROKER_ASSISTED
          ) {
            return this.entrySummaryStateService
              .copyCompanyPoaToEntryIfNeeded({ ...this.entrySummary, dutiesPaymentType: data.dutiesPaymentType })
              .pipe(map(() => data));
          }
          return of(data);
        }),
        switchMap((data: EntrySummaryModel) => {
          if (data.dutiesPaymentType === enumEntrySummaryDutiesPaymentType.ACH) {
            data.payerUnitNumber = this.company.payerUnitNumber;

            return this.entrySummaryStateService
              .copyCompanyPunToEntryIfNeeded({ ...this.entrySummary, dutiesPaymentType: data.dutiesPaymentType })
              .pipe(map(() => data));
          } else {
            data.payerUnitNumber = null;
          }
          return of(data);
        }),
        switchMap((data: EntrySummaryModel) => this.entrySummaryStateService.updateDocuments(true).pipe(map(() => data))),
        switchMap((data: EntrySummaryModel) => {
          return this.entrySummaryStateService.updateEntrySummary(data);
        }),
      )
      .subscribe((entry) => {
        this.entrySummaryStateService.updateCurrentEntrySummary(entry);

        this.appService.closeSidebar();
        this.changeDetectorRef.detectChanges();
      });
  }

  public onEditInvoice(data: { invoice: InvoiceModel; shipment: ShipmentModel }, componentSidebarRefExists?: boolean): void {
    const { invoice, shipment } = data;

    if (!componentSidebarRefExists) {
      this.componentSidebarRef = this.appService.openSidebar(EsInvoiceFormDesktopComponent, {
        sidebarHeaderText: 'LABELS.EDIT_INVOICE_DETAILS',
      });
    } else {
      this.fh.validateAllFields(this.invoiceFg);
    }

    this.getShipmentCurrencyRates(shipment);
    this.currentShipment = shipment;
    this.currentInvoice = invoice;

    this.prepareDataForUpdate(invoice);

    this.componentSidebarRef.instance.invoiceFg = this.invoiceFg;
    this.componentSidebarRef.instance.currencies = this.currencies;
    this.componentSidebarRef.instance.currencyRate = this.currencyRate;
    this.componentSidebarRef.instance.incoterms = this.incoterms;
    this.componentSidebarRef.instance.invoice = this.currentInvoice;
    this.componentSidebarRef.instance.entrySummaryId = this.entrySummary.id;
    this.componentSidebarRef.instance.exportDate = this.currentShipment.exportDate;

    this.componentSidebarRef.instance.invoiceDataSaved
      .pipe(
        takeUntil(merge(this.wizardDestroy$, this.destroy$)),
        switchMap(() => {
          if (!componentSidebarRefExists) {
            this.appService.closeSidebar();
          }

          return this.updateInvoice();
        }),
        switchMap(() => this.entrySummaryStateService.loadEntrySummary(this.entrySummary.referenceNumber)),
      )
      .subscribe((entry) => {
        if (!!entry) {
          this.entrySummaryStateService.updateCurrentEntrySummary(entry);
        }

        if (componentSidebarRefExists) {
          this.entrySummaryStateService.wizardStepNext$.next();
        }
      });
  }

  public onUpdateInvoice(data: { invoice: InvoiceModel; shipment: ShipmentModel }): void {
    const { invoice, shipment } = data;
    this.currentShipment = shipment;
    this.currentInvoice = invoice;

    this.entityFg = this.fh.createEntityModelFormGroup(this.creatingInvoice?.manufacturer?.entityCode || enumEntityModelCode.MANUFACTURER);
    this.entityFg.patchValue(this.creatingInvoice?.manufacturer);

    this.updateOrCreateEntity()
      .pipe(
        switchMap((manufacturer: EntityModel) => {
          const invoiceFormValue: any = this.invoiceFg.getRawValue();
          const invoiceFixed: any = {
            invoiceNumber: invoiceFormValue?.invoiceNumber,
            currency: invoiceFormValue?.currency || DEFAULT_CURRENCY_CODE,
            incoTermsCode: invoiceFormValue?.incoTermsCode,
            manufacturer: manufacturer,
            manufacturerId: manufacturer.id,
            countryOfExportId: invoiceFormValue?.countryOfExportId,
            countryOfExport: invoiceFormValue?.countryOfExport,
          };

          this.prepareDataForUpdate(invoiceFixed);

          return this.updateInvoice();
        }),
        switchMap(() => this.entrySummaryStateService.loadEntrySummary(this.entrySummary.referenceNumber)),
        takeUntil(this.destroy$),
      )
      .subscribe((entry: EntrySummaryModel) => {
        if (!!entry) {
          this.entrySummaryStateService.updateCurrentEntrySummary(entry);
        }
        this.entityFg.reset();
      });
  }

  public onEditEntity(entity: EntityModel, inheritedEntity: EntityModel, code: enumEntityModelCode, keyName: string): void {
    this.componentSidebarRef = this.appService.openSidebar(EsEntityUnifyDefaultDesktopComponent, {
      sidebarHeaderText: this.translateService.instant(DESC_ENTITY[code].titleDesktop),
    });

    this.entityFg = this.fh.createEntityModelFormGroup(code);
    this.fh.updateEntityValidation(true, this.entityFg);

    // Set component properties BEFORE patching form
    this.componentSidebarRef.instance.formGroup = this.entityFg;
    this.componentSidebarRef.instance.radioButton = this.entityFg.get('id');
    this.componentSidebarRef.instance.info = DESC_ENTITY[code];
    this.componentSidebarRef.instance.entityCode = code;
    this.componentSidebarRef.instance.entityKeyName = keyName;
    if (!!inheritedEntity) {
      this.componentSidebarRef.instance.inheritedEntity = inheritedEntity;
    }

    // Determine which entity to use and set radioButton value
    let entityToUse = entity;
    let radioButtonValue = entity?.id || 0;

    // If entity is using inherited entity, set radioButton to inheritedEntity.id
    if (entity?.id === inheritedEntity?.id && !!inheritedEntity) {
      entityToUse = inheritedEntity;
      radioButtonValue = inheritedEntity.id;
    }

    // Patch form with emitEvent: false to prevent triggering valueChanges before component is ready
    this.entityFg.patchValue(entityToUse, { emitEvent: false });
    this.componentSidebarRef.instance.entity = entityToUse;

    // Set radioButton value with emitEvent: false
    this.entityFg.get('id').patchValue(radioButtonValue, { emitEvent: false });
    this.componentSidebarRef.instance.entityDataSaved
      .pipe(
        takeUntil(this.destroy$),
        switchMap((entityData: EntityModel) => {
          this.entityFg.patchValue(entityData, { emitEvent: false });

          return this.updateOrCreateImporter();
        }),
        switchMap((importer: EntityModel) => {
          if (this.entrySummary.importerId !== importer?.id) {
            return this.entrySummaryStateService.updateEntrySummary({ ...this.entrySummary, importerId: importer?.id });
          } else {
            return of(this.entrySummary);
          }
        }),
        switchMap(() => {
          return this.entrySummaryStateService.loadEntrySummary(this.entrySummary.referenceNumber);
        }),
      )
      .subscribe((entry) => {
        this.entrySummaryStateService.updateCurrentEntrySummary(entry);
        this.entityFg.reset();

        this.appService.closeSidebar();
        this.changeDetectorRef.detectChanges();
      });
  }

  public onEditShipmentEntity(
    entity: EntityModel,
    inheritedEntity: EntityModel,
    code: enumEntityModelCode,
    keyName: string,
    shipment: ShipmentModel,
  ): void {
    this.componentSidebarRef = this.appService.openSidebar(EsEntityUnifyDefaultDesktopComponent, {
      sidebarHeaderText: this.translateService.instant(DESC_ENTITY[code].titleDesktop),
    });

    this.entityFg = this.fh.createEntityModelFormGroup(code);
    this.currentShipment = shipment;

    // Set component properties BEFORE patching form to avoid timing issues
    this.componentSidebarRef.instance.formGroup = this.entityFg;
    this.componentSidebarRef.instance.radioButton = this.entityFg.get('id');
    this.componentSidebarRef.instance.info = DESC_ENTITY[code];
    this.componentSidebarRef.instance.entityCode = code;
    this.componentSidebarRef.instance.entityKeyName = keyName;
    if (!!inheritedEntity) {
      this.componentSidebarRef.instance.inheritedEntity = inheritedEntity;
    }

    // Determine which entity to use and set radioButton value
    let entityToUse = entity;
    let radioButtonValue = entity?.id || 0;

    // If entity is using inherited entity (e.g., buyer using importer), set radioButton to inheritedEntity.id
    if (entity?.id === inheritedEntity?.id && !!inheritedEntity) {
      entityToUse = inheritedEntity;
      radioButtonValue = inheritedEntity.id;
    }

    if (!!entityToUse) {
      if (!entityToUse?.address) {
        entityToUse.address = new AddressModel();
        entityToUse.address.id = 0;
        entityToUse.addressId = entityToUse.address.id;
      }

      // Patch form with emitEvent: false to prevent triggering valueChanges before component is ready
      this.entityFg.patchValue(entityToUse, { emitEvent: false });
      this.componentSidebarRef.instance.entity = entityToUse;
    }

    // Set radioButton value with emitEvent: false
    this.entityFg.get('id').patchValue(radioButtonValue, { emitEvent: false });

    this.fh.updateEntityValidation(true, this.entityFg);
    this.componentSidebarRef.instance.entityDataSaved
      .pipe(
        takeUntil(this.destroy$),
        switchMap((entityData: EntityModel) => {
          this.entityFg.patchValue({ ...entityData, code }, { emitEvent: false });

          return this.updateOrCreateEntity(entityData?.id === inheritedEntity?.id);
        }),
        switchMap((data: EntityModel) => {
          return this.entrySummaryStateService.updateShipment(this.currentShipment, { [`${keyName}Id`]: data?.id });
        }),
        switchMap(() => {
          return this.entrySummaryStateService.loadEntrySummary(this.entrySummary.referenceNumber);
        }),
      )
      .subscribe((entry) => {
        this.entrySummaryStateService.updateCurrentEntrySummary(entry);
        this.entityFg.reset();

        this.appService.closeSidebar();
        this.changeDetectorRef.detectChanges();
      });
  }

  public onEditTermsOfSale(shipment: ShipmentModel): void {
    this.componentSidebarRef = this.appService.openSidebar(EsTermsOfSaleDesktopComponent, {
      sidebarHeaderText: 'LABELS.SHIPPING_TERMS_OF_SALE',
    });

    this.componentSidebarRef.instance.entrySummary = this.entrySummary;
    this.componentSidebarRef.instance.shipment = shipment;
    this.componentSidebarRef.instance.dataSaved
      .pipe(
        takeUntil(this.destroy$),
        switchMap((data: ShipmentModel) => this.entrySummaryStateService.updateShipment(shipment, data)),
        switchMap(() => {
          return this.entrySummaryStateService.loadEntrySummary(this.entrySummary.referenceNumber);
        }),
      )
      .subscribe((entry) => {
        this.entrySummaryStateService.updateCurrentEntrySummary(entry);
        this.appService.closeSidebar();
        this.changeDetectorRef.detectChanges();
      });
  }

  public onEditBL(shipment: ShipmentModel): void {
    this.componentSidebarRef = this.appService.openSidebar(EsBillOfLadingFormDesktopComponent, {
      sidebarHeaderText: 'DOCUMENT_TYPE.BILL_OF_LADING',
    });

    this.componentSidebarRef.instance.entrySummary = this.entrySummary;
    this.componentSidebarRef.instance.isCanEdit = this.isCanEdit;
    this.componentSidebarRef.instance.shipment = shipment;
    this.componentSidebarRef.instance.dataSaved
      .pipe(
        takeUntil(this.destroy$),
        switchMap((data: ShipmentModel) => this.entrySummaryStateService.updateShipment(shipment, data)),
        switchMap(() => {
          return this.entrySummaryStateService.loadEntrySummary(this.entrySummary.referenceNumber);
        }),
      )
      .subscribe((entry) => {
        this.entrySummaryStateService.updateCurrentEntrySummary(entry);
        this.appService.closeSidebar();
        this.changeDetectorRef.detectChanges();
      });
  }

  public onRemoveBL(shipmentId: number): void {
    this.entrySummaryStateService.removeShipment(shipmentId);
  }

  public onReviewInvoice(data: { invoice: InvoiceModel; shipment: ShipmentModel }): void {
    let invalidStepsCount: number = 0;
    this.stepsWizardOrder = [];

    if (this.isInvoiceDetailsInvalid(data.invoice)) {
      invalidStepsCount++;
      this.stepsWizardOrder.push({ component: EsInvoiceFormDesktopComponent, dataGeneral: data });
    }

    if (this.isInvoiceMFInvalid(data.invoice)) {
      invalidStepsCount++;
      this.stepsWizardOrder.push({ component: EsEntityUnifyDefaultDesktopComponent, dataGeneral: data });
    }

    if (this.isInvoiceHTSInvalid(data.invoice)) {
      // if lines are missing - add only one sideform
      // if we have multiple invalid lines - create multiple sidebars
      const invalidLines: LineModel[] = (data.invoice?.lines || []).filter((line: LineModel) => !!line.validationMessages?.length);

      invalidLines.forEach((line: LineModel) => {
        invalidStepsCount++;
        this.stepsWizardOrder.push({
          component: EsLineItemFormDesktopComponent,
          dataLines: { invoiceId: data.invoice.id, shipmentId: data.shipment.id, itemId: line.id },
        });
      });
      // if lines are empty
      if (!invalidLines.length) {
        invalidStepsCount++;
        this.stepsWizardOrder.push({
          component: EsLineItemFormDesktopComponent,
          dataLines: { invoiceId: data.invoice.id, shipmentId: data.shipment.id },
        });
      }
    }

    this.componentSidebarRef = this.appService.openSidebar(EsInvoiceStepperComponent, {
      sidebarHeaderText: 'INVOICE_REVIEW_FIX',
    });
    this.componentSidebarRef.instance.totalWizardIndexes = invalidStepsCount;

    if (!!this.stepsWizardOrder.length) {
      this.currentWizardIndex = 0;
      this.handleWizardNextStep();
    }
  }

  private initializeBlModalDocuments(shipmentId: number) {
    this.blModalDocuments = []; //reset

    this.defaultBlModalDocs.forEach((docType, i) => {
      //populate
      this.blModalDocuments.push({
        id: -1 * (i + 1),
        rootEntityId: this.entrySummary.id,
        rootEntityType: enumDocumentRootEntityType.ENTRY_SUMMARY,
        relatedEntityId: shipmentId,
        relatedEntityType: enumDocumentRelatedEntityType.SHIPMENT,
        url: null,
        createdDate: null,
        fileName: null,
        type: docType,
        state: null,
      });
    });
  }

  private uploadBlModalDocuments() {
    return from(this.blModalDocuments).pipe(
      filter((doc) => !!this.blModalInputFiles.get(doc.id)),
      mergeMap((doc) => {
        const isInvoiceUpload = doc.type === enumDocumentType.INVOICE;

        let currentInvoice: InvoiceModel;

        if (isInvoiceUpload) {
          const currentShipment = this.entrySummary?.shipments?.find((sh) => sh.id === this.blModalShipment?.id);
          const invoices = currentShipment?.invoices;
          currentInvoice = invoices?.length === 1 ? invoices[0] : (invoices || []).find((i) => i.id === doc.relatedEntityId);
        }

        const createInvoice$: Observable<InvoiceModel | Partial<InvoiceModel>> = isInvoiceUpload
          ? !!currentInvoice
            ? of(currentInvoice)
            : this.entrySummaryStateService.createInvoice(this.blModalShipment?.id, {})
          : of(null);

        const currentBlModalFile = this.blModalInputFiles.get(doc.id);

        return createInvoice$.pipe(
          switchMap((invoice) => {
            return this.entrySummaryStateService.uploadDocument(
              {
                fileName: currentBlModalFile.name,
                rootEntityId: this.entrySummary?.id,
                rootEntityType: enumDocumentRootEntityType.ENTRY_SUMMARY,
                relatedEntityId: !!invoice ? invoice?.id : this.blModalShipment?.id,
                relatedEntityType: !!invoice ? enumDocumentRelatedEntityType.INVOICE : enumDocumentRelatedEntityType.SHIPMENT,
                type: Number(doc.type),
              },
              currentBlModalFile,
              false,
              this.company?.id,
            );
          }),
        );
      }),
      toArray(),
      takeUntil(this.destroy$),
    );
  }

  private updateInputErrors(file: File) {
    const allowedFileExtensions = ALLOWED_FILE_EXTENSIONS.filter((fe) => !this.fileExtensionIgnoreList.includes(fe));
    const extensionError = this.fileExtensionsValidator(file, allowedFileExtensions);
    const sizeError = this.maxFileSizeValidator(file, environment.files.default_file_size_limit); // 10 MB limit

    if (extensionError) {
      this.fileControl.setErrors(extensionError);
      this.errorMessage = this.translateService.instant('FORM_ERROR.FILE_EXTENSION_NOT_SUPPORTED', { fileName: file.name });
    } else if (sizeError) {
      this.fileControl.setErrors(sizeError);
      this.errorMessage = this.translateService.instant('FORM_ERROR.FILE_SIZE_EXCEEDS_LIMIT', { fileName: file.name });
    } else {
      this.fileControl.setValue(file);
      this.fileControl.setErrors(null);
      this.errorMessage = '';
    }
  }

  private setIsShowInvalidFileToast(value: boolean) {
    this.isShowInvalidFileToast = value;
  }

  public onCloseInvalidFileToast() {
    this.setIsShowInvalidFileToast(false);
  }

  private removeQueryParams(paramsToRemove: string[]) {
    const queryParams = { ...this.route.snapshot.queryParams };

    paramsToRemove.forEach((param) => {
      delete queryParams[param];
    });

    this.router.navigate([], { queryParams });
  }

  private handleWizardNextStep(): void {
    // close sidebar if no more steps left
    if (this.currentWizardIndex > this.stepsWizardOrder.length - 1) {
      this.appService.closeSidebar();
      this.currentWizardIndex = 0;
      this.stepsWizardOrder = [];
      return;
    }

    this.handleWizardStep();
  }

  private handleWizardPrevStep(): void {
    // close sidebar if no more steps left
    if (this.currentWizardIndex < 0) {
      this.appService.closeSidebar();
      this.currentWizardIndex = 0;
      this.stepsWizardOrder = [];
      return;
    }

    this.handleWizardStep();
  }

  private handleWizardStep(): void {
    if (!this.stepsWizardOrder.length || !this.componentSidebarRef) {
      return;
    }

    this.componentSidebarRef.instance.componentRef = null;
    this.componentSidebarRef.instance.currentWizardIndex = this.currentWizardIndex;
    this.componentSidebarRef.instance.nextTitle = this.wizardNextStepText;

    // update existing steps data
    if (!!this.stepsWizardOrder[this.currentWizardIndex]?.dataGeneral?.invoice) {
      this.stepsWizardOrder[this.currentWizardIndex].dataGeneral = {
        ...this.stepsWizardOrder[this.currentWizardIndex].dataGeneral,
        invoice: this.entrySummary?.shipments
          .find((item: ShipmentModel) => item.id === this.stepsWizardOrder[this.currentWizardIndex].dataGeneral.shipment.id)
          ?.invoices?.find((invoice: InvoiceModel) => invoice.id === this.stepsWizardOrder[this.currentWizardIndex].dataGeneral.invoice.id),
      };
    }

    if (this.stepsWizardOrder[this.currentWizardIndex].component === EsInvoiceFormDesktopComponent) {
      this.componentSidebarRef.instance.componentRef = EsInvoiceFormDesktopComponent;
      this.componentSidebarRef.instance.title = 'INVOICE_MAIN_DETAILS';
      this.componentSidebarRef.instance.componentName = enumWizardComponent.invoiceFormDesktopComponent;
      this.onEditInvoice(this.stepsWizardOrder[this.currentWizardIndex].dataGeneral, true);
    }

    if (this.stepsWizardOrder[this.currentWizardIndex].component === EsEntityUnifyDefaultDesktopComponent) {
      this.componentSidebarRef.instance.componentRef = EsEntityUnifyDefaultDesktopComponent;
      this.componentSidebarRef.instance.title = 'MANUFACTURER';
      this.componentSidebarRef.instance.componentName = enumWizardComponent.entityUnifyDefaultDesktopComponent;
      this.onEditManufacturer(this.stepsWizardOrder[this.currentWizardIndex].dataGeneral, true);
    }

    if (this.stepsWizardOrder[this.currentWizardIndex].component === EsLineItemFormDesktopComponent) {
      this.componentSidebarRef.instance.componentRef = EsLineItemFormDesktopComponent;
      // used for '#N - Line item'
      const lineIndex: number =
        this.entrySummary?.shipments
          ?.find((shipment: ShipmentModel) => shipment.id === this.stepsWizardOrder[this.currentWizardIndex].dataLines.shipmentId)
          ?.invoices?.find((invoice: InvoiceModel) => invoice.id === this.stepsWizardOrder[this.currentWizardIndex].dataLines.invoiceId)
          ?.lines?.findIndex((item: LineModel) => item.id === this.stepsWizardOrder[this.currentWizardIndex].dataLines.itemId) + 1;

      this.componentSidebarRef.instance.title = this.translateService.instant('INDEX_LINE_ITEM', {
        index: lineIndex,
      });
      this.componentSidebarRef.instance.componentName = enumWizardComponent.lineItemFormDesktopComponent;
      this.onEditHTS(this.stepsWizardOrder[this.currentWizardIndex].dataLines, true);
    }

    this.componentSidebarRef.instance.runDetection();
  }

  private get wizardNextStepText(): string {
    if (this.currentWizardIndex === this.stepsWizardOrder.length - 1) {
      return 'FINISH';
    }

    if (
      this.stepsWizardOrder[this.currentWizardIndex].component === EsLineItemFormDesktopComponent &&
      this.stepsWizardOrder[this.currentWizardIndex + 1] &&
      this.stepsWizardOrder[this.currentWizardIndex + 1].component === EsLineItemFormDesktopComponent
    ) {
      return 'NEXT_LINE';
    }

    return 'LABELS.NEXT';
  }

  public onEditManufacturer(
    data: { invoice: InvoiceModel; shipment: ShipmentModel; isCreating?: boolean },
    componentSidebarRefExists?: boolean,
  ): void {
    const { invoice, shipment, isCreating } = data;

    if (!componentSidebarRefExists) {
      this.componentSidebarRef = this.appService.openSidebar(EsEntityUnifyDefaultDesktopComponent, {
        sidebarHeaderText: this.translateService.instant(DESC_ENTITY[enumEntityModelCode.MANUFACTURER].titleDesktop),
      });
    } else {
      this.fh.validateAllFields(this.invoiceFg);
    }

    this.currentShipment = shipment;
    if (!!invoice?.manufacturer) {
      this.currentInvoice = invoice;
      this.componentSidebarRef.instance.entity = this.currentInvoice?.manufacturer;
    } else {
      if (!!invoice?.id) {
        this.currentInvoice = invoice;
      }
      if (!!invoice) {
        invoice.manufacturer = new EntityModel({ entityCode: enumEntityModelCode.MANUFACTURER });
      }
    }
    if (!!this.creatingInvoice?.manufacturer) {
      this.currentInvoice = this.creatingInvoice;
      this.componentSidebarRef.instance.entity = this.creatingInvoice?.manufacturer;
    }

    if (!!invoice && !!this.invoiceFg) {
      if (!invoice?.manufacturer?.address) {
        invoice.manufacturer.address = new AddressModel();
      }
      this.invoiceFg.patchValue({ ...invoice });
    }

    this.fh.updateEntityValidation(true, this.invoiceFg.get('manufacturer'));

    this.componentSidebarRef.instance.formGroup = this.invoiceFg.get('manufacturer');
    this.componentSidebarRef.instance.radioButton = this.invoiceFg.get('manufacturerId');
    this.componentSidebarRef.instance.info = DESC_ENTITY[enumEntityModelCode.MANUFACTURER];
    this.componentSidebarRef.instance.entityCode = enumEntityModelCode.MANUFACTURER;
    this.componentSidebarRef.instance.entityKeyName = 'manufacturer';
    this.componentSidebarRef.instance.inheritedEntity = this.currentShipment?.seller;
    this.componentSidebarRef.instance.entityDataSaved
      .pipe(
        takeUntil(merge(this.wizardDestroy$, this.destroy$)),
        switchMap((manufacturer: EntityModel) => {
          this.invoiceFg.get('manufacturer').patchValue(manufacturer);
          this.invoiceFg.get('manufacturerId').setValue(manufacturer.id);

          if (isCreating) {
            const validationMessages = manufacturer?.validationMessages || [];
            let addressValidationMessages = manufacturer?.address?.validationMessages || {};
            // we don't store MF directly (only after invoice save) -> fake validations
            if (!manufacturer.name) {
              validationMessages.push({ text: 'MISSING_COMPANY_NAME', severity: enumValidationMessageSeverity.ERROR });
            } else if (!manufacturer.address?.country) {
              addressValidationMessages = {
                ...addressValidationMessages,
                Country: { text: 'MISSING_COUNTRY', severity: enumValidationMessageSeverity.ERROR },
              };
            } else if (!manufacturer.address?.city) {
              addressValidationMessages = {
                ...addressValidationMessages,
                City: { text: 'MISSING_CITY', severity: enumValidationMessageSeverity.ERROR },
              };
            } else if (!manufacturer.address?.line1) {
              // TODO: !!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!
              addressValidationMessages = {
                ...addressValidationMessages,
                StreetName: { text: 'MISSING_ADDRESS', severity: enumValidationMessageSeverity.ERROR },
              };
            }

            this.creatingInvoice = {
              ...this.creatingInvoice,
              manufacturer: {
                ...manufacturer,
                validationMessages,
                address: {
                  ...manufacturer?.address,
                  validationMessages: addressValidationMessages as any, // TODO fix it later, when refactoring address
                },
              },
              manufacturerId: manufacturer.id,
            };

            if (this.currentShipment?.seller?.id === manufacturer.id) {
              this.creatingInvoice = {
                ...this.creatingInvoice,
                manufacturer: this.currentShipment.seller,
                manufacturerId: this.currentShipment.seller.id,
              };
              this.invoiceFg.get('manufacturer').patchValue(this.creatingInvoice?.manufacturer);
              return of(this.currentShipment.seller);
            } else {
              this.invoiceFg.get('manufacturer').patchValue(this.creatingInvoice?.manufacturer);
              return of(manufacturer);
            }
          }

          if (this.currentShipment?.seller?.id === manufacturer.id) {
            return of(manufacturer);
          } else {
            return this.updateOrCreateManufacturer(manufacturer);
          }
        }),
        switchMap((manufacturer: EntityModel) => {
          if (isCreating) {
            return of(this.invoiceFg.value);
          } else {
            this.invoiceFg.get('manufacturerId').setValue(manufacturer.id);
            return this.updateInvoice();
          }
        }),
        switchMap(() => {
          if (isCreating) {
            return of(this.entrySummary);
          } else {
            return this.entrySummaryStateService.loadEntrySummary(this.entrySummary.referenceNumber);
          }
        }),
      )
      .subscribe((entry) => {
        if (!isCreating) {
          this.entrySummaryStateService.updateCurrentEntrySummary(entry);
          this.invoiceFg.reset({ currency: DEFAULT_CURRENCY_CODE });
        }

        if (!!this.creatingInvoice && !this.creatingInvoice?.id) {
          this.creatingInvoice.id = this.currentInvoice?.id;
        }

        if (!componentSidebarRefExists) {
          this.appService.closeSidebar();
        } else {
          this.entrySummaryStateService.wizardStepNext$.next();
        }

        // Due to big prop drilling data is stuck somewhere at bottom levels
        this.entrySummaryStateService.forceDataReload$.next(true);

        this.changeDetectorRef.detectChanges();
      });
  }

  /** #13384: "Finalize" opens the Validate HTS codes view on the first unverified line of that Bill of Lading. */
  public onFinalize(data: { shipmentId: number; invoiceId: number }): void {
    this.htsLinesReviewService.open({
      focusShipmentId: data.shipmentId,
      focusInvoiceId: data.invoiceId,
      isReadonly: !this.isEditModeOn,
      panelComponent: EsHtsLinePanelComponent,
    });
  }

  public onEditHTS(data: { shipmentId: number; invoiceId: number; itemId?: number }, componentSidebarRefExists?: boolean): void {
    const { shipmentId, invoiceId, itemId } = data;

    if (!componentSidebarRefExists) {
      this.componentSidebarRef = this.appService.openSidebar(EsLineItemFormDesktopComponent, {
        sidebarHeaderText: this.translateService.instant('LABELS.HTS_CODE_LINE_ITEM'),
      });
    }

    this.componentSidebarRef.instance.itemId = itemId || null;
    this.componentSidebarRef.instance.shipmentId = shipmentId || null;
    this.componentSidebarRef.instance.invoice = (
      (this.entrySummary?.shipments || []).find((shipment: ShipmentModel) => shipment.id === shipmentId)?.invoices || []
    ).find((invoice: InvoiceModel) => invoice.id === invoiceId);

    this.componentSidebarRef.instance.currentShipment = this.entrySummary.shipments.find(
      (shipment: ShipmentModel) => shipment.id === shipmentId,
    );
    this.componentSidebarRef.instance.validateImmediately = componentSidebarRefExists;
    // #13383 F6: "Find HTS" may persist a new line before Save; the wizard step must reopen with that id, not POST again
    this.componentSidebarRef.instance.lineCreated
      .pipe(takeUntil(merge(this.wizardDestroy$, this.destroy$)))
      .subscribe((createdId: number) => {
        const step: { dataLines?: { itemId?: number } } = this.stepsWizardOrder[this.currentWizardIndex];
        if (componentSidebarRefExists && step && step.dataLines) {
          step.dataLines.itemId = createdId;
        }
      });
    this.componentSidebarRef.instance.lineDataSaved
      .pipe(
        takeUntil(merge(this.wizardDestroy$, this.destroy$)),
        switchMap((lineItem: LineModel) => this.updateOrCreateLineItem(lineItem, invoiceId)),
        switchMap(() => this.entrySummaryStateService.loadEntrySummary(this.entrySummary.referenceNumber)),
      )
      .subscribe((entry) => {
        this.entrySummaryStateService.updateCurrentEntrySummary(entry);

        if (!componentSidebarRefExists) {
          this.appService.closeSidebar();
        } else {
          this.entrySummaryStateService.wizardStepNext$.next();
        }

        this.changeDetectorRef.markForCheck();
      });
  }

  public onDeleteHTS(lineItemId: number): void {
    this.entrySummaryStateService
      .deleteLineItem(lineItemId)
      .pipe(
        switchMap(() => this.entrySummaryStateService.loadEntrySummary(this.entrySummary.referenceNumber)),
        takeUntil(this.destroy$),
      )
      .subscribe((entry: EntrySummaryModel) => {
        this.entrySummaryStateService.updateCurrentEntrySummary(entry);
        this.changeDetectorRef.detectChanges();
      });
  }

  // Bulk delete is performed by the hts table itself; here we only refetch the ES so the
  // shipments/invoices and the invoice summary cards reflect the removed lines.
  public onLinesBulkDeleted(): void {
    this.entrySummaryStateService
      .loadEntrySummary(this.entrySummary.referenceNumber)
      .pipe(takeUntil(this.destroy$))
      .subscribe((entry: EntrySummaryModel) => {
        this.entrySummaryStateService.updateCurrentEntrySummary(entry);
        this.changeDetectorRef.detectChanges();
      });
  }

  public declineModal() {
    if (this.isCreateBL) {
      this.onSubmitNewBL(false);
    } else {
      this.appService.closeSidebar();
    }
  }

  public clearStep() {
    if (!this.isCreateBL) {
      this.appService.closeSidebar();
    }
  }

  public saveIntermediateDesktopData(): void {
    const isShowAllOcrConfirm: boolean = (this.entrySummary?.shipments || []).some(
      (shipment: ShipmentModel) => !this.checkIsOcrConfirmed(shipment),
    );
    if (isShowAllOcrConfirm && !this.isHasEsOcrRedesign) {
      this.openOcrAllConfirmModal = true;
      this.isAllOcrConfirmCtrl.setValue(false);
      return;
    } else {
      this.openOcrAllConfirmModal = false;
    }

    this.esShipmentDetailsDesktopComponent.saveBonds();

    this.entrySummaryStateService
      .updateEntrySummary({
        ...this.entrySummary,
        dutiesPaymentType: this.fg.get('dutiesPaymentType').value,
        importerId: this.fg.get('importerId').value,
      })
      .pipe(take(1))
      .subscribe((entry) => {
        this.entrySummaryStateService.updateCurrentEntrySummary(entry);
        this.router.navigate(['entry-summary', entry.referenceNumber]);
      });
  }

  public saveBLData(): void {
    this.router.navigate(['entry-summary', this.entrySummary?.referenceNumber], { queryParams: { [IS_CREATING_ES_KEY]: true } });

    this.scrollTop();
  }

  public addNewInvoice(blId: number): void {
    if (this.isHasEsOcrRedesign) {
      this.addDocStripInvoice(blId);
    } else {
      this.currentEditedBillOfLadingId = blId;
      this.invoiceFg.reset({ currency: DEFAULT_CURRENCY_CODE });
      this.invoiceFg
        .get('invoiceNumber')
        .setValidators([
          Validators.required,
          Validators.maxLength(17),
          CustomValidator.notMatchValues(this.allInvoiceNumbers, 'invoiceNumberMatch'),
        ]);
      this.isEditInvoice = true;
      this.editInvoiceChange.emit(this.isEditInvoice);
      setTimeout(() => this.newBLForm.nativeElement.scrollIntoView());
    }
  }

  public addDocStripInvoice(shipmentId: number) {
    const shipment = this.entrySummary.shipments?.find((sh) => sh.id === shipmentId);
    const invoices = shipment?.invoices || [];
    const data = {
      document: {
        id: -1, //default invoice id
        rootEntityId: this.entrySummary.id,
        rootEntityType: enumDocumentRootEntityType.ENTRY_SUMMARY,
        relatedEntityId: shipmentId,
        relatedEntityType: enumDocumentRelatedEntityType.INVOICE,
        url: null,
        createdDate: null,
        fileName: null,
        type: enumDocumentType.INVOICE,
        state: null,
      },
      shipmentId: shipmentId,
      isForceAdd: true,
    };

    this.entrySummaryStateService.uploadDocStripDocument(data);
  }

  public removeNewInvoice(): void {
    this.isEditInvoice = false;
    this.editInvoiceChange.emit(this.isEditInvoice);
    this.currentEditedBillOfLadingId = null;
  }

  public saveTemporaryInvoice(shipment: ShipmentModel, data?: { document: DocumentModel; file: File }): void {
    if (this.invoiceFg.get('invoiceNumber').invalid) {
      this.invoiceFg.markAllAsTouched();

      return;
    }

    const { document, file } = data;
    this.updateOrCreateManufacturer(this.invoiceFg.get('manufacturer').value)
      .pipe(
        take(1),
        switchMap((manufacturer) => {
          this.invoiceFg.patchValue({ manufacturer, manufacturerId: manufacturer.id });

          if (!!shipment) {
            this.currentShipment = shipment;
          }

          return this.createNewInvoice();
        }),
        switchMap((invoice) => {
          this.newlyCreatedInvoicesIds.push(invoice.id);

          if (!!document && !!file) {
            return this.entrySummaryStateService.uploadDocument(
              { ...document, relatedEntityId: invoice.id, rootEntityId: this.entrySummary.id },
              file,
              false,
            );
          } else {
            return of(true);
          }
        }),
        switchMap(() => {
          return this.entrySummaryStateService.loadEntrySummary(this.entrySummary.referenceNumber);
        }),
      )
      .subscribe((entry) => {
        this.entrySummaryStateService.updateCurrentEntrySummary(entry);
        this.creatingInvoice = null;
        this.invoiceFg.reset();
        this.isEditInvoice = false;
        this.editInvoiceChange.emit(this.isEditInvoice);
        this.hasTemporaryUploadedDoc = false;
        this.changeDetectorRef.detectChanges();
      });
  }

  public saveTemporaryInvoiceDoc(val: boolean): void {
    this.hasTemporaryUploadedDoc = val;
  }

  public onClearInvoice(): void {
    this.invoiceFg.reset({ currency: DEFAULT_CURRENCY_CODE });
    this.isEditInvoice = true;
    this.editInvoiceChange.emit(this.isEditInvoice);
    this.creatingInvoice = null;
    this.currentInvoice = null;
  }

  public get isHasEsOcrRedesign(): boolean {
    return this.companiesService
      .getUser()
      ?.betaFlags?.map((flag: UserBetaFlagModel) => flag.tag)
      .includes(enumUserBetaFlag.esOcrRedesign);
  }

  public get canSaveNewInvoice(): boolean {
    // #10183
    return true;
  }

  public get isBillOfLadingRequired(): boolean {
    return this.entrySummary?.submissionType === enumEntrySummarySubmissionType.BROKER_ASSISTED && !this.isTruck;
  }

  public get isBlModalContinueDisabled(): boolean {
    const shipments = this.entrySummary?.shipments;

    if (!shipments || !this.blModalDocuments || !this.blModalShipment) {
      return;
    }

    let isAnyBlEmptyOfDocs: boolean = false;

    const requiredDocTypes = [enumDocumentType.INVOICE];
    if (this.isBillOfLadingRequired) {
      requiredDocTypes.push(enumDocumentType.BILL_OF_LADING);
    }
    const shipmentDocs = this.blModalDocuments?.filter((doc) => {
      const isInvoiceUpload = doc.type === enumDocumentType.INVOICE;
      let invoice: InvoiceModel;
      if (isInvoiceUpload) {
        const currentShipment = this.entrySummary?.shipments?.find((sh) => sh.id === this.blModalShipment?.id);
        const invoices = currentShipment?.invoices;
        invoice = invoices?.find((i) => i.id === doc.relatedEntityId);
      }
      return isInvoiceUpload && !!invoice
        ? doc.relatedEntityId === invoice.id && doc.state === enumDocumentState.CREATED
        : doc.relatedEntityId === this.blModalShipment?.id && doc.state === enumDocumentState.CREATED;
    });

    if (!!shipmentDocs && !shipmentDocs.length) {
      isAnyBlEmptyOfDocs = true;
    }

    return isAnyBlEmptyOfDocs;
  }

  public get missingBlModalRequiredDocuments(): Map<number, enumDocumentType[]> {
    const missingRequiredDocs: Map<number, enumDocumentType[]> = new Map();

    const shipments = this.entrySummary?.shipments;

    if (!shipments || !this.blModalDocuments || !this.blModalShipment) {
      return;
    }

    const requiredMissingDocTypes: enumDocumentType[] = [];
    const currentShipmentDocuments = this.blModalDocuments?.filter((doc) => {
      const isInvoiceUpload = doc.type === enumDocumentType.INVOICE;
      let invoice: InvoiceModel;
      if (isInvoiceUpload) {
        const currentShipment = this.entrySummary?.shipments?.find((sh) => sh.id === this.blModalShipment.id);
        const invoices = currentShipment?.invoices;
        invoice = invoices?.find((i) => i.id === doc.relatedEntityId);
      }
      return isInvoiceUpload && !!invoice ? doc.relatedEntityId === invoice.id : doc.relatedEntityId === this.blModalShipment.id;
    });

    const isBillOfLadingUploaded = currentShipmentDocuments?.find(
      (doc) => enumDocumentType.BILL_OF_LADING === doc.type && doc.state === enumDocumentState.CREATED,
    );

    const isCommercialInvoiceUploaded = currentShipmentDocuments?.find(
      (doc) => enumDocumentType.INVOICE === doc.type && doc.state === enumDocumentState.CREATED,
    );

    if (this.isBillOfLadingRequired && !isBillOfLadingUploaded) {
      requiredMissingDocTypes.push(enumDocumentType.BILL_OF_LADING);
    }

    if (!isCommercialInvoiceUploaded) {
      requiredMissingDocTypes.push(enumDocumentType.INVOICE);
    }

    missingRequiredDocs.set(this.blModalShipment.id, requiredMissingDocTypes);

    return missingRequiredDocs;
  }

  public get numberOfBlsWitMissingDocuments(): number {
    if (!this.missingBlModalRequiredDocuments) {
      return 0;
    }

    return Array.from(this.missingBlModalRequiredDocuments.values()).filter((arr) => arr.length > 0).length;
  }

  public get singleBlMissingRequiredDocumentNames(): string {
    const shipments = this.entrySummary?.shipments;

    if (!shipments || !this.blModalDocuments || !this.blModalShipment) {
      return '';
    }

    return this.blMissingRequiredDocumentNames();
  }

  public onCreateDeliveryOrder(): void {
    this.router.navigate(['entry-summary', this.entrySummary.referenceNumber, 'delivery-order']);
  }

  public onMessageToBroker(): void {
    this.router.navigate(['entry-summary', this.entrySummary.referenceNumber, 'custom-broker']);
  }

  public onOpenChat(): void {
    this.componentSidebarRef = this.appService.openSidebar(EsChatDesktopComponent, {
      sidebarHeaderText: 'BUTTON.MESSAGE_BROKER',
    });
  }

  public onDuplicate(): void {
    this.entrySummaryRepository
      .duplicate(this.entrySummary.id)
      .pipe(
        switchMap((res) => {
          return res.submissionType === enumEntrySummarySubmissionType.BROKER_ASSISTED
            ? this.documentsService.copyEntryPoaToEntry(this.entrySummary.id, res.id).pipe(map(() => res))
            : of(res);
        }),
        switchMap((res) => {
          return res.submissionType === enumEntrySummarySubmissionType.BROKER_ASSISTED &&
            res.dutiesPaymentType === enumEntrySummaryDutiesPaymentType.ACH
            ? this.documentsService.copyEntryPunToEntry(this.entrySummary.id, res.id).pipe(map(() => res))
            : of(res);
        }),
        takeUntil(this.destroy$),
      )
      .subscribe((res) => {
        this.ga.emitCustomEvent(
          enumGATagEventCategory.ES,
          enumGATagEventAction.ESDuplicate,
          `${res.referenceNumber} FROM ${this.entrySummary.referenceNumber}`,
        );
        this.navigateToDuplicatedEs(res.referenceNumber);
      });
  }

  public get currentShipments(): ShipmentModel[] {
    // if we are not on summary, but on create - filter only one BL
    return !!this.creatingInvoiceBLId && this.entrySummary.shipments.length > 1
      ? this.entrySummary?.shipments?.filter((item) => item.id !== this.creatingInvoiceBLId)
      : this.entrySummary?.shipments;
  }

  public get isOnSummary(): boolean {
    return this.esStepsService.isOnSummary;
  }

  public get isCreating(): boolean {
    return this.esStepsService.isCreating;
  }

  public get isCreatingInvoice(): boolean {
    return this.esStepsService.isCreatingInvoice;
  }

  public get creatingInvoiceBLId(): number {
    return this.esStepsService.creatingInvoiceBLId;
  }

  public get isPaymentPending(): boolean {
    return this.entrySummary?.paymentState === enumPaymentState.PENDING;
  }

  public onInvoiceSaved(): void {
    this.isInvoiceSaved = true;
    this.isEditInvoice = false;
    this.editInvoiceChange.emit(this.isEditInvoice);
    this.currentEditedBillOfLadingId = null;
  }

  public onReplaceInvoice(blId: number, data: { invoiceId: number; file: File }): void {
    this.changeDetectorRef.detectChanges();

    this.entrySummaryStateService
      .removeInvoice(blId, data.invoiceId)
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.isEditInvoice = true;
        this.editInvoiceChange.emit(this.isEditInvoice);
        this.currentEditedBillOfLadingId = blId;
        this.newlyCreatedInvoicesFile = data.file;
        this.changeDetectorRef.detectChanges();
      });
  }

  public trackByBLFn(i: ShipmentModel): number {
    return i.id;
  }

  public onSubmitNewBL(needValidate: boolean = true) {
    if (needValidate && this.createBLFormGroup.invalid) {
      const invalidFields = this.fh.validateAllFields(this.createBLFormGroup);
      this.openNewBLModal(invalidFields);
      return;
    }

    this.entrySummaryStateService
      .updateShipment(this.newShipment, this.createBLFormGroup.value)
      .pipe(
        take(1),
        switchMap(() => this.entrySummaryStateService.loadEntrySummary(this.entrySummary.referenceNumber)),
      )
      .subscribe((data) => {
        this.esStepsService.isCreatingBL = false;
        this.entrySummaryStateService.updateCurrentEntrySummary(data);
        this.isNewShipmentLoaded = false;
        this.isCreateBL = false;
        this.removeQueryParams([IS_CREATING_ES_INVOICE_KEY]);
        this.changeDetectorRef.detectChanges();
      });
  }

  public isBLNewlyCreated(blId: number): boolean {
    return (
      !this.esStepsService.isCreating &&
      !this.esStepsService.isCreatingInvoice &&
      !this.esStepsService.creatingInvoiceBLId &&
      blId === this.newShipment?.id
    );
  }

  public onDownloadAllFiles(documentsIds: number[]): void {
    this.documentsService
      .getArchive(this.entrySummary.referenceNumber, documentsIds)
      .pipe(take(1))
      .subscribe((file: Blob) => {
        this.documentsService.downloadBlobFile(file, this.documentsService.formatZipFileName(this.entrySummary.referenceNumber));
      });
  }

  public isInvoiceValid(shipment: ShipmentModel): boolean {
    return (shipment?.invoices || []).every((invoice: InvoiceModel) => !this.isShowErrorForInvoice(this.checkValidation(invoice)));
  }

  public isCreatingInvoiceStepInvalid(): boolean {
    return this.entrySummaryStateService.isCreatingInvoiceStepInvalid(this.entrySummary);
  }

  public isShowBLAddCardDesktop(shipment: ShipmentModel): boolean {
    return (
      this.isInvoiceSaved && !this.isEditInvoice && shipment?.invoices?.length > 0 && !this.isCreateBL && !this.isESSynced && this.isCanEdit
    );
  }

  public checkValidation(invoice: InvoiceModel): ValidationMessageModel[] {
    return [
      ...invoice?.validationMessages,
      ...(invoice?.manufacturer?.validationMessages || []),
      ...Object.values(invoice?.manufacturer?.address?.validationMessages || []).map((message: any) => message?.text),
      ...this.checkInvoiceLinesValidation(invoice),
    ];
  }

  private checkInvoiceLinesValidation(invoice: InvoiceModel): ValidationMessageModel[] {
    const grossWeightMessage: string = 'GROSS_WEIGHT_MISSING';
    const result: ValidationMessageModel[] = [];

    (invoice.lines || []).forEach((line: LineModel) => {
      result.push(...line.validationMessages.filter((lineItem: ValidationMessageModel) => lineItem.text !== grossWeightMessage));
    });

    return result;
  }

  public isShowErrorForInvoice(validationMessages: ValidationMessageModel[]): boolean {
    return !!(validationMessages || [])?.filter((item: ValidationMessageModel) => item.text !== 'INVOICE_OCR_STATE_NOT_REVIEWED')?.length;
  }

  public isShowBLEditableCardDesktop(shipment: ShipmentModel): boolean {
    return (this.isEditInvoice && shipment?.id === this.currentEditedBillOfLadingId) || shipment?.invoices?.length === 0;
  }

  public isShowBLBody(shipment: ShipmentModel): boolean {
    return this.isShowBLAddCardDesktop(shipment) || this.isShowBLEditableCardDesktop(shipment);
  }

  public get isInsuranceAvailable(): Observable<boolean> {
    return this.marineInsuranceState.isInsuranceAvailable;
  }

  public get isProviderWasDown(): Observable<boolean> {
    return this.marineInsuranceState.isProviderWasDown;
  }

  public get currentInsurance(): BehaviorSubject<MarineInsuranceModel> {
    return this.marineInsuranceState.currentInsurance;
  }

  public get isInsuranceLoading(): Observable<boolean> {
    return this.marineInsuranceState.isInsuranceLoading;
  }

  public get currentInsuranceDocuments(): BehaviorSubject<DocumentModel> {
    return this.marineInsuranceState.currentInsuranceDocuments;
  }

  public get currentInsurancePendingBills(): BehaviorSubject<PaymentBillItemPendingModel> {
    return this.marineInsuranceState.currentInsurancePendingBills;
  }

  public openMarineInsuranceQuoteForm(isEdit?: boolean): void {
    this.marineInsuranceSideformService.onOpenQuoteSidebar(
      enumMarineInsuranceEntityType.ENTRY_SUMMARY,
      {
        entrySummary: this.entrySummary,
      },
      this.marineInsuranceState.currentInsurance.getValue(),
      isEdit,
    );
  }

  public get isEsSent(): boolean {
    return EntrySummaryStatusHelper.isSent(this.entrySummary?.state);
  }

  public get isWithVia(): boolean {
    return this.entrySummary?.portOfEntryCode !== this.entrySummary?.portOfUnladingCode;
  }

  public get isTruck(): boolean {
    return this.entrySummary?.transportationMode === enumTransportationMode.TRUCK;
  }

  public checkIsShowOcrConfirm(shipment: ShipmentModel): boolean {
    return (shipment?.invoices || []).some((invoice: InvoiceModel) =>
      [enumOcrStatus.PROCESSED, enumOcrStatus.PARTIALLY_PROCESSED, enumOcrStatus.EXTRACTION_FAILED, enumOcrStatus.REVIEWED].includes(
        invoice.ocrStatus,
      ),
    );
  }

  public checkIsOcrConfirmed(shipment: ShipmentModel): boolean {
    return (shipment?.invoices || []).every((invoice: InvoiceModel) =>
      [enumOcrStatus.REVIEWED, enumOcrStatus.CANCELED].includes(invoice.ocrStatus),
    );
  }

  public onFinishOCR(): void {
    this.newlyCreatedInvoicesFile = null;
    this.entrySummaryStateService
      .loadEntrySummary(this.entrySummary.referenceNumber)
      .pipe(takeUntil(this.destroy$))
      .subscribe((entry: EntrySummaryModel) => {
        this.entrySummaryStateService.updateCurrentEntrySummary(entry);
        this.isEditInvoice = false;
        this.editInvoiceChange.emit(this.isEditInvoice);
        this.currentEditedBillOfLadingId = null;
        this.isInvoiceSaved = false;
        this.changeDetectorRef.markForCheck();
      });
  }

  private openNewBLModal(data: any): void {
    this.missingFieldModal.openCustomModal(
      this.esValidationsHelperService.getEsSummaryModalModifiedData(this.entrySummary.transportationMode, data),
    );
  }

  private uploadDocument(document: DocumentCreateModel, file: File) {
    return this.entrySummaryStateService.uploadDocument(document, file, this.pendingBills.length > 0);
  }

  private uploadPoaOrPunDocument(document: DocumentCreateModel, file: File, uploadToCompany: boolean) {
    if (document.type === enumDocumentType.POWER_OF_ATTORNEY) {
      return this.uploadPoaDocument(document, file, uploadToCompany);
    } else {
      return this.uploadPunDocument(document, file, uploadToCompany);
    }
  }

  private uploadPoaDocument(document: DocumentCreateModel, file: File, uploadToCompany: boolean) {
    return this.entrySummaryStateService.uploadPoaDocument(document, file, uploadToCompany, this.pendingBills.length > 0);
  }

  private uploadPunDocument(document: DocumentCreateModel, file: File, uploadToCompany: boolean) {
    return this.entrySummaryStateService.uploadPunDocument(document, file, uploadToCompany, this.pendingBills.length > 0);
  }

  private getDeliveryOrderInfo() {
    forkJoin(this.entrySummary.shipments.map((shipment) => this.deliveryOrdersRepository.getByShipment(shipment.id)))
      .pipe(take(1))
      .subscribe((deliveryOrders) => {
        this.deliveryOrdersInfo = flatten(deliveryOrders);
      });
  }

  private getDuties() {
    this.entrySummaryStateService
      .getDuties(this.entrySummary.id)
      .pipe(takeUntil(this.destroy$))
      .subscribe((duties) => {
        this.dutiesValue = duties;
        this.loadedDuties$.next(true);

        this.changeDetectorRef.detectChanges();
      });
  }

  private navigateToDuplicatedEs(referenceNumber: string) {
    const url = this.router
      .createUrlTree(['entry-summary', referenceNumber], {
        queryParams: { isDuplicate: true },
      })
      .toString();

    this.location.go(url);
    this.location.forward();

    setTimeout(() => {
      window.location.reload();
    });
  }

  private getNavExtras(validateAfterInit: boolean = false) {
    return {
      queryParams: {
        returnUrl: `entry-summary/${this.entrySummary.referenceNumber}`,
        validateAfterInit,
      },
    };
  }

  private updateInvoice(newInvoiceData?: Partial<InvoiceModel>) {
    if (!newInvoiceData) {
      newInvoiceData = { ...this.invoiceFg.value };
    }

    return this.invoicesRepository.update(this.currentInvoice.id, newInvoiceData);
  }

  private createNewInvoice() {
    const newInvoiceData = {
      ...this.invoiceFg.value,
      manufacturerId: this.invoiceFg.value?.manufacturer?.id,
    };

    return this.invoicesRepository.create(this.currentShipment.id, newInvoiceData);
  }

  private updateOrCreateImporter(): Observable<EntityModel> {
    const entity: EntityModel = this.entityFg.value;

    if (!!this.entrySummary?.importerId) {
      return this.entrySummaryStateService.updateEntity(this.entrySummary.importerId, entity);
    } else {
      return this.entrySummaryStateService.createEntity(entity);
    }
  }

  private updateOrCreateEntity(isAsParent?: boolean): Observable<EntityModel> {
    const entity: EntityModel = this.entityFg.getRawValue();
    const entityId = this.entityFg.get('id').value;

    if (isAsParent) {
      return of(entity);
    } else if (!!entityId) {
      return this.entrySummaryStateService.updateEntity(entity?.id, entity);
    } else {
      return this.entrySummaryStateService.createEntity(entity);
    }
  }

  private updateOrCreateManufacturer(manufacturer: EntityModel): Observable<EntityModel> {
    const manufacturerId = manufacturer?.id;

    if (manufacturerId === 0 || manufacturerId === null) {
      manufacturer.addressId = 0;
      manufacturer.address.id = 0;
      manufacturer.entityCode = enumEntityModelCode.MANUFACTURER;
      return this.entrySummaryStateService.createEntity(manufacturer);
    } else if (this.currentShipment?.seller?.id === manufacturerId) {
      return of(this.currentShipment.seller);
    } else {
      return this.entrySummaryStateService.updateEntity(manufacturerId, manufacturer);
    }
  }

  private getCurrencies() {
    this.currenciesRepository
      .getCurrencies()
      .pipe(takeUntil(this.destroy$))
      .subscribe((currencies) => {
        this.currencies = currencies;
      });
  }

  private getShipmentCurrencyRates(shipment: ShipmentModel) {
    const allCurrenciesCodes: string[] = shipment.invoices
      .reduce((prev, curr) => UtilsHelper.concat<string>(prev, curr.currency), [])
      .filter((code) => code !== DEFAULT_CURRENCY_CODE);

    if (allCurrenciesCodes && allCurrenciesCodes.length) {
      forkJoin(allCurrenciesCodes.map((code) => this.currenciesRepository.getRate(code, shipment.exportDate || DateHelper.now)))
        .pipe(take(1))
        .subscribe((currencies: CurrencyRateModel[]) => {
          currencies.filter((currency) => !!currency).forEach((currency) => this.ratesMap.set(currency.code, currency));
        });
    }
  }

  private loadInvoiceIncoterms() {
    this.codeRepository
      .getIncoterms()
      .pipe(take(1))
      .subscribe((incoterms) => {
        this.incoterms = incoterms;

        this.filterIncoterms();
      });
  }

  // TODO: check detectChanges circle, necessary tick() to update currency rate in the es-details-invoice-editable-card-desktop
  private checkCurrency() {
    const currencyControl: UntypedFormControl = this.invoiceFg.get('currency') as UntypedFormControl;
    if (currencyControl && currencyControl.value) {
      const currency = currencyControl.value;
      if (!currency || currency === DEFAULT_CURRENCY_CODE) {
        return;
      }
      const exportDate = this.currentShipment?.exportDate ? this.currentShipment.exportDate : DateHelper.now;

      this.currenciesRepository
        .getRate(currency, exportDate)
        .pipe(take(1))
        .subscribe((currencyRate) => {
          this.currencyRate = currencyRate ? currencyRate.rate : NaN;

          if (!!this.componentSidebarRef?.instance?.setCurrencyRate) {
            this.componentSidebarRef.instance?.setCurrencyRate(this.currencyRate);
          }

          if (isNaN(this.currencyRate)) {
            currencyControl.patchValue(DEFAULT_CURRENCY_CODE);
            this.openCurrencyWarningRateModal = true;
            this.currencyWarningRateModalData = {
              exportDate: this.customDatePipe.transform(exportDate),
              currency,
            };
          }
          this.changeDetectorRef.detectChanges();
        });
    }
  }

  private filterIncoterms() {
    if (this.incoterms && this.incoterms.length && this.entrySummary.transportationMode) {
      this.incotermsByType = this.incoterms.filter((incoterm) =>
        this.entrySummary.transportationMode === enumTransportationMode.OCEAN
          ? [enumIncotermApplicableType.ALL, enumIncotermApplicableType.OCEAN].includes(incoterm.applicableTransport)
          : [enumIncotermApplicableType.ALL].includes(incoterm.applicableTransport),
      );
    }
  }

  private updateOrCreateLineItem(data: LineModel, invoiceId?: number): Observable<LineModel> {
    if (data.id) {
      return this.updateLineItem(data);
    } else {
      return this.createNewLineItem(data, invoiceId);
    }
  }

  private updateLineItem(data): Observable<LineModel> {
    return this.entrySummaryStateService.updateLineItem(data, data.id);
  }

  private createNewLineItem(data, invoiceId: number): Observable<LineModel> {
    return this.entrySummaryStateService.createLineItem(data, invoiceId);
  }

  private getCbpLogMessages(): void {
    this.entrySummaryMessagesRepository
      .getCbpRawMessages(this.entrySummary.id, false, this.genericService.isPrinting$.getValue())
      .pipe(take(1))
      .subscribe((messages) => {
        this.cbpLogMessages = messages;
      });
  }

  private scrollTop() {
    window.scroll(0, 0);
  }

  private getInvoiceNumbers(): string[] {
    const result: string[] = [];

    if (this.entrySummary.shipments && this.entrySummary.shipments.length) {
      for (const shipment of this.entrySummary.shipments) {
        result.push(...shipment.invoices.map((invoice: InvoiceModel) => invoice.invoiceNumber));
      }
    }

    return UtilsHelper.uniq<string>(result);
  }

  private getInsuranceState(isReset?: boolean): void {
    if (isReset) {
      this.marineInsuranceState.saveData(null, true, false);
    }

    this.entrySummaryStateService
      .getInsuranceAvailability(this.entrySummary)
      .pipe(
        switchMap((data: { insurance: MarineInsuranceModel; isAvailable: boolean }) =>
          combineLatest([
            of(data),
            !!data.insurance?.referenceNumber
              ? this.entrySummaryStateService.getInsurancePendingBills(this.company?.id, data.insurance.referenceNumber)
              : of(EMPTY),
          ]),
        ),
        takeUntil(this.destroy$),
      )
      .subscribe(
        ([data, pendingBills]: [{ insurance: MarineInsuranceModel; isAvailable: boolean }, PaymentBillItemPendingModel]) => {
          if (!!data.insurance?.certificate?.issueDate) {
            this.marineInsuranceState.saveData(data.insurance, data.isAvailable, true);
            this.loadInsuranceDocuments$.next();
          } else {
            this.marineInsuranceState.saveData(data.insurance, data.isAvailable, false);
          }

          this.marineInsuranceState.saveBillsData(pendingBills);
        },
        () => this.marineInsuranceState.saveData(null, true, false),
      );
  }

  private listenPaymentActions(): void {
    this.paymentState.successPayment$
      .pipe(
        // #12668 a POA purchase started from this page is not a payment of this entry
        filter((data: { state: enumPaymentState; bill: BillModel }) => !!data && !isPoaBill(data.bill)),
        take(1),
      )
      .subscribe((data: { state: enumPaymentState; bill: BillModel }) => {
        this.onSuccessPayment.emit(data);
      });

    this.paymentState.errorCustomPayment$
      .pipe(
        filter((error: ICustomError) => !!error),
        take(1),
      )
      .subscribe((error: ICustomError) => {
        this.onErrorCustomPayment.emit(error);
      });

    this.paymentState.errorCallback$
      .pipe(
        filter((error: ICustomError) => !!error),
        take(1),
      )
      .subscribe((error: ICustomError) => {
        this.onErrorPayment.emit(error);
      });

    this.paymentState.cancelCallback$
      .pipe(
        filter((error: ICustomError) => !!error),
        take(1),
      )
      .subscribe(() => {
        this.cancelPayment();
      });

    this.paymentState.addToCard$
      .pipe(
        filter((data: boolean) => !!data),
        take(1),
      )
      .subscribe(() => {
        this.addToCard();
      });
  }

  private updateTruckValidation(): void {
    if (this.isTruck) {
      this.createBLFormGroup.removeControl('seller');

      if (!this.isWithVia) {
        this.createBLFormGroup.get('itDate').clearValidators();
        this.createBLFormGroup.get('itNumber').clearValidators();
      }
    }

    this.createBLFormGroup.updateValueAndValidity();
  }

  private prepareDataForUpdate(invoice: InvoiceModel): void {
    if (!!invoice && !!this.invoiceFg) {
      this.invoiceFg.patchValue({
        invoiceNumber: invoice?.invoiceNumber,
        currency: invoice?.currency || DEFAULT_CURRENCY_CODE,
        incoTermsCode: invoice?.incoTermsCode,
        manufacturer: invoice?.manufacturer,
        manufacturerId: invoice?.manufacturerId,
        countryOfExportId: invoice?.countryOfExportId,
        countryOfExport: invoice?.countryOfExport,
      });

      this.invoiceFg.get('invoiceNumber').setValidators([
        Validators.required,
        Validators.maxLength(17),
        CustomValidator.notMatchValues(
          this.allInvoiceNumbers.filter((item: string) => item !== invoice.invoiceNumber),
          'invoiceNumberMatch',
        ),
      ]);
    }
  }

  private isInvoiceDetailsInvalid(invoice: InvoiceModel): boolean {
    if (!invoice) {
      return;
    }

    return !invoice.invoiceNumber || !invoice.currency || !invoice.countryOfExportId;
  }

  private isInvoiceMFInvalid(invoice: InvoiceModel): boolean {
    if (!invoice) {
      return;
    }

    return (
      !invoice.manufacturerId ||
      !!invoice.manufacturer?.validationMessages?.length ||
      !!Object.keys(invoice.manufacturer?.address?.validationMessages || {}).length
    );
  }

  private isInvoiceHTSInvalid(invoice: InvoiceModel): boolean {
    if (!invoice) {
      return;
    }

    return !invoice.lines?.length || (invoice.lines || []).some((line) => !!line.validationMessages?.length);
  }

  private fileExtensionsValidator(file: File, allowedExtensions: string[]): ValidationErrors | null {
    const extension = file.name.split('.').pop()?.toLowerCase() as string;

    return allowedExtensions.includes(extension) ? null : { fileExtensionNotAllowed: true };
  }

  private maxFileSizeValidator(file: File, maxFileSizeMB: number): ValidationErrors | null {
    const fileSizeMB = file.size / 1024 / 1024;

    return fileSizeMB > maxFileSizeMB
      ? { maxFileSize: { currentSize: Math.round(fileSizeMB * 100) / 100, maxFileSize: maxFileSizeMB } }
      : null;
  }
}
